import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { track, ANALYTICS_EVENTS } from '../../utils/productAnalytics';
import {
  CUSTOM_IMAGE_SIZE_HINT,
  CUSTOM_IMAGE_SIZE_MAX,
  CUSTOM_IMAGE_SIZE_MIN,
  formatCustomImageSize,
  validateCustomImageSize,
} from '../../utils/customImageSize';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Sidebar from '../../components/sidebar/Sidebar';
import imageGenerationService, { GeneratedImage, ImageHistoryOptions, ImageHistorySort, SamplePrompt, type StyleRating } from '../../services/api/imageGenerationService';
import { ProductImageSelectionModal, SelectedProductImage, ProductInfoCopyModal, StyleSelectModal, prefetchStyleModalData, SelectedStyle, ProductSelectionModal, ProductSelectionData, MagicResizeModal, GenerationInputModeTabs } from '../../components/image-generation';
import BannerLibraryModal from '../../components/image-generation/BannerLibraryModal';
import BannerDetailPanel from '../../components/image-generation/BannerDetailPanel';
import BannerAbTestStartModal from '../../components/banner-ab-test/BannerAbTestStartModal';
import StyleFeedbackButtons from '../../components/image-generation/StyleFeedbackButtons';
import BannerImageEditModal from '../../components/image-generation/BannerImageEditModal';
import ImageVersionNavigator from '../../components/image-generation/ImageVersionNavigator';
import GeneratingJobsSection from '../../components/image-generation/GeneratingJobsSection';
import JobDetailModal from '../../components/image-generation/JobDetailModal';
import LayerExportModal from '../../components/image-generation/LayerExportModal';
import knowledgeApiService from '../../services/api/knowledge/knowledgeApiService';
import {
  fetchAutoStyles,
  filterMaterialsByStyle,
  getDiverseStyleCandidateLimit,
  pickRandomDistinctStyles,
  type AutoStyleResponse,
} from '../../services/api/autoStyleService';
import { ImageIcon, Download, Upload, FolderOpen, X, FileText, Users, User, Sparkles, Copy, HelpCircle, Heart, ArrowDownAZ, Check, FlaskConical } from 'lucide-react';
import { useSelector } from 'react-redux';
import { selectOrganization } from '../../redux/organization/organizationSlice';
import { shouldShowPrompt } from '../../utils/environment';
import { buildCompetitorAnalysisContext } from '../../utils/competitorAnalysisContext';
import BannerVideoWorkspace from '../../components/image-generation/BannerVideoWorkspace';
import BannerVideoDialog from '../../components/image-generation/BannerVideoDialog';
import { ResponsiveAppShell, TaskyButton, TaskyCheckbox, TaskySelect, TaskyTabs, TaskyTopBar, useConfirm, type TaskyTabItem, TaskyPageContainer } from '../../components/tasky';
import ThrottledImage from '../../components/common/ThrottledImage';
import productImageService from '../../services/api/productImageService';
import { selectLogoForBackground, isLogoToneConflicting, LogoTone, BackgroundTone } from '../../utils/logoToneSelector';
import { useToast } from '../../contexts/ToastContext';
import onboardingV2Service from '../../services/api/onboardingV2Service';
import CelebrationConfetti from '../../components/common/CelebrationConfetti';
import BannerOnboardingGuideModal, {
  shouldShowBannerOnboardingGuide,
} from './components/BannerOnboardingGuideModal';
import { useSetAdmanPageContext } from '../../components/adman/useAdmanPageContext';
import { useAdmanRefreshHandler } from '../../components/adman/useAdmanRefreshHandler';
import BannerStyleChoice, { BannerStyleMode } from './components/BannerStyleChoice';
import { loadAllStyles, recommendedStyleToSelected, resolveStylePrompts, useBannerStylePreview } from './components/useBannerStylePreview';
import { BANNER_PROMPT_TEMPLATE, getBannerReadiness } from './components/bannerReadiness';
import TouchUpHint from './components/TouchUpHint';
import { behavior } from '../../analytics';
import { detectTouchUpIntent } from '../../utils/touchUpIntent';
import { generatedImageTitle } from '../../utils/generatedImageTitle';
import { BANNER_IMAGE_MODEL_OPTIONS, DEFAULT_BANNER_IMAGE_MODEL, type BannerImageModel } from '../../constants/imageModel';
import {
  availableImageCountOptions,
  clampImageCount,
  concurrencyNotice,
} from '../../utils/imageCountOptions';
import type { ConcurrencyLimits } from '../../services/api/imageGenerationService';
import TaapBannerHandoff from '../../components/taap/TaapBannerHandoff';
import { ImageTagList } from '../../components/image-generation/ImageTagMenu';
import BulkTagBar from '../../components/image-generation/BulkTagBar';
import ImageTagBar from '../../components/image-generation/ImageTagBar';
import type { ImageTag, ImageTagAssignment } from '../../services/api/imageTagService';
import { useBannerTags } from './components/useBannerTags';

const IMAGE_SIZE_PRESETS = [
  {
    value: '1080x1080',
    ratio: '1:1',
    label: '正方形',
    dimensions: '1080×1080',
    useCase: 'Instagram Feed / 汎用バナー',
    previewClass: 'aspect-square w-9',
  },
  {
    value: '1080x1350',
    ratio: '4:5',
    label: '縦長',
    dimensions: '1080×1350',
    useCase: 'Instagram / Meta広告',
    previewClass: 'aspect-[4/5] w-8',
  },
  {
    value: '1080x1920',
    ratio: '9:16',
    label: '縦長',
    dimensions: '1080×1920',
    useCase: 'Story / Reels / Shorts',
    previewClass: 'aspect-[9/16] w-7',
  },
  {
    value: '1920x1080',
    ratio: '16:9',
    label: '横長',
    dimensions: '1920×1080',
    useCase: 'YouTube / LPメイン',
    previewClass: 'aspect-video w-12',
  },
];

const formatImageSizeLabel = (size: string): string => {
  const [width, height] = size.split('x').map(Number);
  if (!width || !height) return size;
  return `横${width}×縦${height}px`;
};

const getSizeDirection = (width: number, height: number): string => {
  if (!width || !height) return '未設定';
  if (width === height) return '正方形';
  return width > height ? '横長' : '縦長';
};

const getSimplifiedRatio = (width: number, height: number): string => {
  if (!width || !height) return '-';
  const gcd = (a: number, b: number): number => (b === 0 ? a : gcd(b, a % b));
  const divisor = gcd(width, height);
  return `${Math.round(width / divisor)}:${Math.round(height / divisor)}`;
};

// Minimal logo shape used for auto tone-matching. Both ProductImage variants
// in this codebase (services/api/productImageService.ts, types/knowledge.ts)
// carry all of these fields, so either can be mapped into this without loss.
interface LogoCandidateEntry {
  id: number;
  url: string;
  file_path: string;
  file_name: string;
  is_primary: boolean;
  sort_order: number;
  logo_tone?: LogoTone | null;
}

const buildLogoSwapMessage = (backgroundTone: BackgroundTone, replacementTone: LogoTone | null | undefined): string => {
  const bgLabel = backgroundTone === 'dark' ? '背景が暗いスタイル' : '背景が明るいスタイル';
  if (replacementTone === 'color') {
    return `${bgLabel}のため、カラーのロゴに自動で切り替えました`;
  }
  const toneLabel = replacementTone === 'light' ? '明るい色' : '暗い色';
  return `${bgLabel}のため、${toneLabel}のロゴに自動で切り替えました`;
};

/** ABテスト1回あたりに選べるバナーの上限（ペア比較の回数が増えすぎないように） */
const AB_TEST_MAX_IMAGES = 4;

/**
 * 一覧の「ABテストモード」（選んだ数枚のペア比較）の入口。ABテストは別の画面で始められるようにするので止めている。
 * 詳細の「オートABテスト」は残す。戻すときは true にし、テストの abTestModeTest も test に戻す
 */
const SHOW_AB_TEST_MODE = false;

const BannerAdsPage: React.FC = () => {
  const { showToast } = useToast();
  const confirm = useConfirm();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // Form state
  const [prompt, setPrompt] = useState('');
  const [imageSize, setImageSize] = useState('1080x1080');
  const [error, setError] = useState<string | null>(null);
  const [showUpgradeCta, setShowUpgradeCta] = useState(false);
  const [isStartingGeneration, setIsStartingGeneration] = useState(false);
  const [selectedStyle, setSelectedStyle] = useState<SelectedStyle | null>(null);
  const [selectedStyles, setSelectedStyles] = useState<SelectedStyle[]>([]);
  // スタイル未選択が既定（商品に合わせて自動選択）。見本で選ぶと manual、解除すると戻る。
  // manual 以外は商品モードなら auto、フリーテキストなら none（ランダム）として扱う（effectiveStyleMode）。
  const [styleMode, setStyleMode] = useState<BannerStyleMode>('auto');
  const [textlessMode, setTextlessMode] = useState(false);
  const [proMode, setProMode] = useState(false);
  const [bannerImageModel, setBannerImageModel] = useState<BannerImageModel>(DEFAULT_BANNER_IMAGE_MODEL);

  // Sample prompts
  const [materialPrompts, setMaterialPrompts] = useState<SamplePrompt[]>([]);
  const [bannerPrompts, setBannerPrompts] = useState<SamplePrompt[]>([]);

  // Input mode
  const [inputMode, setInputMode] = useState<'product' | 'freetext'>('product');

  // Custom image size
  const [useCustomSize, setUseCustomSize] = useState(false);
  const [customWidth, setCustomWidth] = useState('1080');
  const [customHeight, setCustomHeight] = useState('1080');

  // Product selection
  const [selectedProductData, setSelectedProductData] = useState<ProductSelectionData | null>(null);
  const [additionalInstructions, setAdditionalInstructions] = useState('');

  // 広告自動化マンに「今この商品を見ている」と伝える（F20-a）。既存ロジックには触れない。
  useSetAdmanPageContext(
    selectedProductData ? { product_ids: [selectedProductData.product.id] } : null
  );

  // Reference images
  const [referenceImages, setReferenceImages] = useState<SelectedProductImage[]>([]);
  const [uploadedReferenceImages, setUploadedReferenceImages] = useState<Array<{
    id: string;
    file?: File;
    preview: string;
    base64: string;
    s3Key?: string;
    category: 'brand_logo' | 'product_image' | 'material';
    /** 「画像を使う」で追加した生成済みバナー。手直しの案内で編集機能を開くために持つ */
    sourceImage?: GeneratedImage;
  }>>([]);
  const uploadInputRef = useRef<HTMLInputElement>(null);
  const promptSectionRef = useRef<HTMLDivElement>(null);

  // Logo tone auto-select (背景トーンに合わせたロゴ自動差し替え)
  // true になったら「現在のスタイルに対しては、これ以上自動差し替えしない」= 直近のユーザー選択を最終決定として尊重する。
  // スタイル変更で false にリセットする。
  const [logoAutoSwapLocked, setLogoAutoSwapLocked] = useState(false);
  const logoCandidatesCacheRef = useRef<Map<number, LogoCandidateEntry[]>>(new Map());
  const lastAutoStyleIdsRef = useRef<number[]>([]);

  // Generation count
  const [requestedImageCount, setImageCount] = useState(3);
  // 同時実行枠。ジョブ一覧のポーリングに相乗りして受け取る（追加リクエストは無い）
  const [concurrencyLimits, setConcurrencyLimits] = useState<ConcurrencyLimits | null>(null);
  const [styleVariationSource, setStyleVariationSource] = useState<{
    sourceImageId: number;
    styleId?: number;
    styleName?: string;
  } | null>(null);

  // Modal state
  const [showImageSelectionModal, setShowImageSelectionModal] = useState(false);
  const [showBannerLibraryModal, setShowBannerLibraryModal] = useState(false);
  const [showProductInfoModal, setShowProductInfoModal] = useState(false);
  const [showStyleSelectModal, setShowStyleSelectModal] = useState(false);
  const [showProductSelectionModal, setShowProductSelectionModal] = useState(false);
  const [showMagicResizeModal, setShowMagicResizeModal] = useState(false);
  const [showLayerModal, setShowLayerModal] = useState(false);
  const [showImageEditModal, setShowImageEditModal] = useState(false);

  // History state
  const [historyView, setHistoryView] = useState<'my' | 'organization' | 'favorites'>('my');
  const [historySort, setHistorySort] = useState<ImageHistorySort>('newest');
  // 商品とタグの絞り込み。どのタブにも同じ条件をかける（タグは URL に残すので useBannerTags が持つ）
  const [productFilter, setProductFilter] = useState<number | null>(null);
  const [filterProducts, setFilterProducts] = useState<Array<{ id: number; name: string }>>([]);
  // 並び・絞り込みを変えたときや、付け外ししたバナーが今の絞り込みから外れたときに増やして1ページ目から取り直す
  const [historyReloadToken, setHistoryReloadToken] = useState(0);
  const historyReloadTokenRef = useRef(historyReloadToken);
  historyReloadTokenRef.current = historyReloadToken;
  // 一覧ごとに最後に出した取得だけを受け付ける（条件を切り替えた直後に前の応答が後から届いても混ぜない）
  const myHistoryRequestRef = useRef(0);
  const orgHistoryRequestRef = useRef(0);
  // 読み足しの判定は state ではなく ref で読む。タブを切り替えた直後のスクロールは前の描画の値（読み込み前）を
  // 持ったまま呼ばれ、1ページ目の読み込み中に2ページ目を取りに行って、1ページ目の応答を古いものとして捨てていた
  const myHistoryLoadingRef = useRef(false);
  const orgHistoryLoadingRef = useRef(false);
  const [displayedContent, setDisplayedContent] = useState<GeneratedImage[]>([]);
  const [organizationImages, setOrganizationImages] = useState<GeneratedImage[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [orgCurrentPage, setOrgCurrentPage] = useState(1);
  const [orgHasMore, setOrgHasMore] = useState(true);
  const [isOrgLoading, setIsOrgLoading] = useState(false);
  const [selectedImage, setSelectedImage] = useState<GeneratedImage | null>(null);

  // 手直しの案内: 生成済みバナーを参照画像にして手直しっぽい指示を書いたら、
  // 全体を作り直す前に「画像を編集」「マジックリサイズ」を案内する（閉じたら同じ組み合わせでは出さない）
  const [dismissedTouchUpHints, setDismissedTouchUpHints] = useState<Set<string>>(new Set());
  const loggedTouchUpHints = useRef<Set<string>>(new Set());
  const touchUpSource = useMemo(
    () => [...uploadedReferenceImages].reverse().find(img => img.sourceImage)?.sourceImage ?? null,
    [uploadedReferenceImages],
  );
  const touchUpIntent = inputMode === 'freetext' && touchUpSource ? detectTouchUpIntent(prompt) : null;
  const touchUpHintKey = touchUpSource && touchUpIntent ? `${touchUpSource.id}:${touchUpIntent}` : null;
  const showTouchUpHint = touchUpHintKey !== null && !dismissedTouchUpHints.has(touchUpHintKey);

  useEffect(() => {
    if (!showTouchUpHint || !touchUpHintKey || loggedTouchUpHints.current.has(touchUpHintKey)) return;
    loggedTouchUpHints.current.add(touchUpHintKey);
    behavior.action('banner_touchup_hint_shown', { intent: touchUpIntent });
  }, [showTouchUpHint, touchUpHintKey, touchUpIntent]);

  const handleOpenTouchUpTool = () => {
    if (!touchUpSource || !touchUpIntent) return;
    setSelectedImage(touchUpSource);
    if (touchUpIntent === 'resize') {
      setShowMagicResizeModal(true);
    } else {
      setShowImageEditModal(true);
    }
  };

  const handleDismissTouchUpHint = () => {
    if (!touchUpHintKey) return;
    setDismissedTouchUpHints(prev => new Set(prev).add(touchUpHintKey));
  };

  const [bulkSelectedIds, setBulkSelectedIds] = useState<Set<number>>(new Set());
  const [isBulkDownloading, setIsBulkDownloading] = useState(false);
  const [abTestOpen, setAbTestOpen] = useState(false);
  // 自動改善ループの起点にするバナー。null ならループ用モーダルは閉じている
  const [autoAbTestImageId, setAutoAbTestImageId] = useState<number | null>(null);
  // ABテストの候補選択。一括ダウンロードの bulkSelectedIds とは完全に独立で、
  // ペアの並びを選んだ順のままにするため Set ではなく配列で持つ。
  // （state の配列は再レンダーでも参照が変わらないので、見積りの取り直しも起きない）
  const [abTestMode, setAbTestMode] = useState(false);
  const [abSelectedIds, setAbSelectedIds] = useState<number[]>([]);
  const abSelectedIdsRef = useRef<number[]>([]);
  // 一括選択モード（タグをまとめて付ける）。ABテストモードと同じくカードの押下が選択になる。
  // 一括ダウンロードの bulkSelectedIds とは別に持つ（左上のチェックはモードの外だけで使う）
  const [selectMode, setSelectMode] = useState(false);
  const [selectModeIds, setSelectModeIds] = useState<Set<number>>(new Set());
  // カードの押下を選択に使うモード中（詳細を開かず、左上のチェックとホバー操作を隠す）
  const pickingMode = abTestMode || selectMode;
  const organization = useSelector(selectOrganization);

  // タグの付け外し・名前や色の変更を、表示中のどの一覧と詳細にも反映する
  const updateImagesEverywhere = useCallback((update: (image: GeneratedImage) => GeneratedImage) => {
    setDisplayedContent(prev => prev.map(update));
    setOrganizationImages(prev => prev.map(update));
    setSelectedImage(prev => (prev ? update(prev) : prev));
  }, []);
  // 付け外しの結果は hook より前に受け取るので、その時点の絞り込みは ref で読む
  const tagFilterIdsRef = useRef<number[]>([]);
  const handleTagsAssigned = useCallback((assignments: ImageTagAssignment[]) => {
    const tagsByImageId = new Map(assignments.map(assignment => [assignment.id, assignment.tags]));
    updateImagesEverywhere(image => {
      const tags = tagsByImageId.get(image.id);
      return tags ? { ...image, tags } : image;
    });
    // 絞り込み中にタグを外して条件に合わなくなったら1ページ目から取り直す
    // （手元で抜くだけだと、次のページがその枚数分ずれて読み飛ばす）
    const filterIds = tagFilterIdsRef.current;
    if (filterIds.length === 0) return;
    const unmatched = assignments
      .filter(assignment => !filterIds.every(id => assignment.tags.some(tag => tag.id === id)))
      .map(assignment => assignment.id);
    if (unmatched.length === 0) return;
    const dropUnmatched = (prev: Set<number>) => {
      const next = new Set(prev);
      unmatched.forEach(id => next.delete(id));
      return next;
    };
    setBulkSelectedIds(dropUnmatched);
    setSelectModeIds(dropUnmatched);
    resetHistoryLists();
  }, [updateImagesEverywhere]);
  const handleTagChanged = useCallback((changed: ImageTag) => {
    updateImagesEverywhere(image => image.tags?.some(tag => tag.id === changed.id)
      ? { ...image, tags: image.tags.map(tag => (tag.id === changed.id ? changed : tag)) }
      : image);
  }, [updateImagesEverywhere]);
  const handleTagDeleted = useCallback((tagId: number) => {
    updateImagesEverywhere(image => image.tags?.some(tag => tag.id === tagId)
      ? { ...image, tags: image.tags.filter(tag => tag.id !== tagId) }
      : image);
  }, [updateImagesEverywhere]);
  const bannerTags = useBannerTags({
    enabled: Boolean(organization?.id),
    onAssigned: handleTagsAssigned,
    onTagChanged: handleTagChanged,
    onTagDeleted: handleTagDeleted,
  });
  const tagFilterIds = bannerTags.filterIds;
  tagFilterIdsRef.current = tagFilterIds;
  const tagFilterKey = tagFilterIds.join(',');
  const historyFilters: ImageHistoryOptions = {
    sort: historySort,
    tagIds: tagFilterIds,
    productId: productFilter ?? undefined,
  };
  // 生成完了後の取り直しは、呼んだ時点とタブ・条件が変わっていれば捨てる
  const historyFilterKey = `${historyView}|${historySort}|${tagFilterKey}|${productFilter ?? ''}`;
  const historyFilterKeyRef = useRef(historyFilterKey);
  historyFilterKeyRef.current = historyFilterKey;
  const [jobDetailJobId, setJobDetailJobId] = useState<string | null>(null);
  const [jobsRefreshTrigger, setJobsRefreshTrigger] = useState(0);
  const [celebrationActive, setCelebrationActive] = useState(false);

  const imageCountOptions = useMemo(() => availableImageCountOptions(concurrencyLimits), [concurrencyLimits]);
  const concurrencyMessage = useMemo(() => concurrencyNotice(concurrencyLimits), [concurrencyLimits]);

  // 希望枚数を保持しつつ、表示と送信には現在選べる枚数を使う。
  const imageCount = clampImageCount(requestedImageCount, concurrencyLimits);
  // 「商品に合わせて自動選択」は商品モードだけ。フリーテキストでは登録済みスタイルからランダムに選ぶ
  const effectiveStyleMode: BannerStyleMode = styleMode === 'manual' ? 'manual' : inputMode === 'product' ? 'auto' : 'none';
  const autoStyleEnabled = effectiveStyleMode === 'auto';
  // 自動で使うスタイルを生成前に選んでおく（生成もこの顔ぶれで行う）
  const stylePreview = useBannerStylePreview({
    mode: effectiveStyleMode,
    productId: inputMode === 'product' ? selectedProductData?.product.id ?? null : null,
    imageCount,
    inputText: prompt,
  });

  // 自動選択の候補が1件も無いとき、生成ボタンを押す前に具体的な理由を見せる
  // （不足している素材の役割・実寸・縦横比など）。スタイルが見つかり次第、
  // 古い理由表示は消す（商品を切り替えた後に前の商品の理由が残らないように）。
  // 消すのはこの effect が出した表示だけで、生成失敗など別のエラーは残す。
  const shownStyleReasonRef = useRef<string | null>(null);
  useEffect(() => {
    if (stylePreview.loading) return;
    const reason = effectiveStyleMode === 'auto' ? stylePreview.noEligibleStyleReason : null;
    if (reason) {
      shownStyleReasonRef.current = reason;
      setError(reason);
      return;
    }
    const shown = shownStyleReasonRef.current;
    if (shown) {
      shownStyleReasonRef.current = null;
      setError((current) => (current === shown ? null : current));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveStyleMode, stylePreview.loading, stylePreview.noEligibleStyleReason]);

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // マウント時にスタイルモーダルのデータを事前取得し、初回オープンを即時表示にする。
  useEffect(() => {
    void prefetchStyleModalData('banner');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-open JobDetailModal when ?job=<uuid> query param is present
  // (used when navigating from /home 最近の作業 → このプロジェクトを開く)
  useEffect(() => {
    const jobId = searchParams.get('job');
    if (jobId) {
      setJobDetailJobId(jobId);
      const next = new URLSearchParams(searchParams);
      next.delete('job');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // /home オンボーディングの「自分でバナーを3枚作る」導線(?onboarding=1)や初回訪問時に、
  // バナー作成の3ステップを案内するモーダルを出す。表示可否は shouldShowBannerOnboardingGuide
  // に集約(純関数・テスト済み)。seen は localStorage で永続化し、閉じたら再表示しない。
  const [bannerGuideSeen] = useState<boolean>(
    () => typeof window !== 'undefined' && window.localStorage.getItem('tasky_banner_guide_seen') === '1',
  );
  const [bannerGuideDismissed, setBannerGuideDismissed] = useState(false);
  const [manualGuideOpen, setManualGuideOpen] = useState(searchParams.get('guide') === '1');
  const [bannerGuideGenerateDone, setBannerGuideGenerateDone] = useState<boolean | null>(null);
  const bannerOnboardingParam = searchParams.get('onboarding') === '1';

  // 条件(b)(初回訪問かつ未作成)の判定に必要な時だけ progress を取得する。
  // ?onboarding=1 の明示導線や既読時は取得不要。取得失敗時は null のままで出さない。
  useEffect(() => {
    if (bannerOnboardingParam || bannerGuideSeen) {
      return undefined;
    }
    let cancelled = false;
    onboardingV2Service
      .getProgress()
      .then((progress) => {
        if (!cancelled) {
          setBannerGuideGenerateDone(progress.generate_done);
        }
      })
      .catch(() => {
        /* 取得失敗時はモーダルを出さない(null のまま) */
      });

    return () => {
      cancelled = true;
    };
  }, [bannerOnboardingParam, bannerGuideSeen]);

  const showBannerGuide = manualGuideOpen || shouldShowBannerOnboardingGuide({
    onboardingParam: bannerOnboardingParam,
    dismissed: bannerGuideDismissed,
    seen: bannerGuideSeen,
    generateDone: bannerGuideGenerateDone,
  });

  const dismissBannerGuide = useCallback(() => {
    setBannerGuideDismissed(true);
    setManualGuideOpen(false);
    try {
      window.localStorage.setItem('tasky_banner_guide_seen', '1');
    } catch {
      /* localStorage 使用不可でも致命的ではないため無視 */
    }
    if (searchParams.has('onboarding') || searchParams.has('guide')) {
      const next = new URLSearchParams(searchParams);
      next.delete('onboarding');
      next.delete('guide');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Fetch both material and banner sample prompts
  useEffect(() => {
    const fetchSamplePrompts = async () => {
      try {
        const [materialData, bannerData] = await Promise.all([
          imageGenerationService.getSamplePrompts('material'),
          imageGenerationService.getSamplePrompts('banner'),
        ]);
        setMaterialPrompts(materialData);
        setBannerPrompts(bannerData);
      } catch (err) {
        console.error('Failed to fetch sample prompts:', err);
      }
    };
    fetchSamplePrompts();
  }, []);

  useEffect(() => {
    if (historyView === 'my') {
      fetchImageHistory();
    }
  }, [currentPage, historyView, historyReloadToken]);

  useEffect(() => {
    if (historyView === 'organization' || historyView === 'favorites') {
      fetchOrganizationImageHistory();
    }
  }, [orgCurrentPage, historyView, historyReloadToken]);

  // タグの絞り込み（URL の ?tags）が変わったら、商品の絞り込みと同じく選択を外して取り直す
  const appliedTagFilterKeyRef = useRef(tagFilterKey);
  useEffect(() => {
    if (appliedTagFilterKeyRef.current === tagFilterKey) return;
    appliedTagFilterKeyRef.current = tagFilterKey;
    setBulkSelectedIds(new Set());
    setSelectModeIds(new Set());
    resetHistoryLists();
  }, [tagFilterKey]);

  useEffect(() => {
    knowledgeApiService.getProducts()
      .then((products) => setFilterProducts((products ?? []).map((product) => ({ id: product.id, name: product.name }))))
      .catch((err) => console.error('Failed to load products for the banner filter:', err));
  }, []);

  const fetchImageHistory = async () => {
    const requestId = ++myHistoryRequestRef.current;
    const isLatest = () => requestId === myHistoryRequestRef.current;
    myHistoryLoadingRef.current = true;
    try {
      setIsLoading(true);
      const response = await imageGenerationService.getImageHistory(currentPage, 'banner_ads', historyFilters);
      if (!isLatest()) return;
      if (response.success) {
        const newImages = response.data.data;
        if (currentPage === 1) {
          setDisplayedContent(newImages);
        } else {
          setDisplayedContent(prev => {
            const existingIds = new Set(prev.map(img => img.id));
            const uniqueNewImages = newImages.filter(img => !existingIds.has(img.id));
            return [...prev, ...uniqueNewImages];
          });
        }
        setTotalPages(response.data.last_page);
        setHasMore(currentPage < response.data.last_page);
      }
    } catch (err: any) {
      console.error('Failed to fetch image history:', err);
    } finally {
      if (isLatest()) {
        myHistoryLoadingRef.current = false;
        setIsLoading(false);
      }
    }
  };

  const fetchOrganizationImageHistory = async () => {
    const requestId = ++orgHistoryRequestRef.current;
    const isLatest = () => requestId === orgHistoryRequestRef.current;
    orgHistoryLoadingRef.current = true;
    try {
      setIsOrgLoading(true);
      const response = await imageGenerationService.getOrganizationImageHistory(orgCurrentPage, 'banner_ads', {
        favorite: historyView === 'favorites',
        ...historyFilters,
      });
      if (!isLatest()) return;
      if (response.success) {
        const newImages = response.data.data;
        if (orgCurrentPage === 1) {
          setOrganizationImages(newImages);
        } else {
          setOrganizationImages(prev => {
            const existingIds = new Set(prev.map(img => img.id));
            const uniqueNewImages = newImages.filter(img => !existingIds.has(img.id));
            return [...prev, ...uniqueNewImages];
          });
        }
        setOrgHasMore(orgCurrentPage < response.data.last_page);
      }
    } catch (err: any) {
      console.error('Failed to fetch organization image history:', err);
    } finally {
      if (isLatest()) {
        orgHistoryLoadingRef.current = false;
        setIsOrgLoading(false);
      }
    }
  };

  // 広告自動化マンの「更新する」ボタンから呼べるようにする（F23-c）。表示中のビューの
  // 先頭ページを取り直す（historyView切替のhandleHistoryViewChangeと同じ考え方）。
  const refreshBannerHistory = () => {
    if (historyView === 'my') {
      if (currentPage === 1) {
        fetchImageHistory();
      } else {
        setCurrentPage(1);
      }
    } else if (orgCurrentPage === 1) {
      fetchOrganizationImageHistory();
    } else {
      setOrgCurrentPage(1);
    }
  };
  useAdmanRefreshHandler(refreshBannerHistory);

  // The entered size is used as-is: out of range values are refused up front
  // (customSizeError) instead of being clamped. Clamping silently is what
  // turned a requested 2400x1256 banner into a 2048x1256 one, with the wrong
  // aspect ratio and no message shown.
  const getEffectiveImageSize = (): string => (
    useCustomSize ? formatCustomImageSize(customWidth, customHeight) : imageSize
  );

  const customSizeError = useMemo(
    () => (useCustomSize ? validateCustomImageSize(customWidth, customHeight) : null),
    [useCustomSize, customWidth, customHeight],
  );

  const readiness = getBannerReadiness({
    starting: isStartingGeneration, mode: inputMode, hasProduct: Boolean(selectedProductData),
    prompt, styleMode, styleCount: selectedStyles.length, imageCount, sizeError: customSizeError,
    capacityMessage: concurrencyMessage ?? '生成上限を確認しています。しばらくお待ちください。',
    productFetching: effectiveStyleMode === 'auto' && stylePreview.productFetching,
  });

  const goToMissingField = () => {
    if (readiness?.target === 'product') setShowProductSelectionModal(true);
    if (readiness?.target === 'style') setShowStyleSelectModal(true);
    const fieldId = readiness?.target === 'prompt' ? 'banner-prompt'
      : readiness?.target === 'count' ? 'banner-image-count' : readiness?.target === 'size' ? 'banner-custom-width' : null;
    if (fieldId) document.getElementById(fieldId)?.focus();
  };

  const handleGenerateMultipleImages = async (e: React.FormEvent) => {
    e.preventDefault();
    // 自動スタイルの事前取得が終わっていなくても止めない（取得は数秒かかる）。足りない分は下でその場で取る
    if (readiness) return;
    setIsStartingGeneration(true);
    setError(null);
    setShowUpgradeCta(false);

    try {
      if (customSizeError) {
        setError(customSizeError);
        return;
      }

      // 別タブや別の生成画面で消費された枠も、ジョブ投入前に再確認する。
      const { limits } = await imageGenerationService.getJobsWithLimits(undefined, 'banner_ads');
      setConcurrencyLimits(limits ?? null);
      if (!limits || imageCount > clampImageCount(imageCount, limits)) {
        setError(limits
          ? '生成できる枚数が変わりました。枚数を確認して再度生成してください。'
          : '生成上限を確認できませんでした。しばらくしてから再度お試しください。');
        return;
      }

      const effectiveSize = getEffectiveImageSize();
      const sizeLabel = useCustomSize
        ? `カスタム (${formatImageSizeLabel(effectiveSize)})`
        : imageSize === '1080x1080' ? '1:1（横1080×縦1080px）' :
          imageSize === '1080x1350' ? '4:5（横1080×縦1350px）' :
          imageSize === '1080x1920' ? '9:16（横1080×縦1920px）' :
          imageSize === '1920x1080' ? '16:9（横1920×縦1080px）' : '1:1（横1080×縦1080px）';

      const [width, height] = effectiveSize.split('x').map(Number);
      let aspectRatioApi: string | undefined;
      if (width === height) {
        aspectRatioApi = '1:1';
      } else if (width > height) {
        aspectRatioApi = width / height >= 1.7 ? '16:9' : '4:3';
      } else {
        aspectRatioApi = height / width >= 1.7 ? '9:16' : '3:4';
      }

      // Prepare reference images
      const allRefImages: Array<{
        type: 'brand_logo' | 'product_image' | 'material';
        s3_key?: string;
        image_data?: string;
      }> = [];

      referenceImages.forEach((img) => {
        allRefImages.push({ type: img.category, s3_key: img.s3Key });
      });

      uploadedReferenceImages.forEach((img) => {
        if (img.s3Key) {
          allRefImages.push({ type: img.category, s3_key: img.s3Key });
        } else {
          allRefImages.push({ type: img.category, image_data: img.base64 });
        }
      });

      // Product mode - delegate prompt generation to job queue
      if (inputMode === 'product' && selectedProductData) {
        try {
          let includePersonApi: string | undefined;
          if (selectedProductData.includePerson === 'include_person' || selectedProductData.includePerson === 'person_using' || selectedProductData.includePerson === 'person_happy') {
            includePersonApi = 'with_person';
          } else if (selectedProductData.includePerson === 'no_person') {
            includePersonApi = 'without_person';
          }

          const appealAxisText = selectedProductData.appealName
            ? (selectedProductData.appealDirection ? `${selectedProductData.appealName} - ${selectedProductData.appealDirection}` : selectedProductData.appealName)
            : undefined;

          const referenceImagesMetadata = [
            ...(selectedProductData.selectedImages?.map(img => {
              const isFileName = /\.(png|jpg|jpeg|svg|gif|webp)$/i.test(img.title || img.file_name || '');
              return {
                category: img.category as 'brand_logo' | 'product_image' | 'material',
                description: !isFileName && img.title ? img.title : undefined,
              };
            }) || []),
            ...uploadedReferenceImages.map(img => ({
              category: img.category,
              description: undefined,
            })),
          ];

          const combinedAdditionalContext = [
            selectedProductData.additionalContext,
            additionalInstructions,
            buildCompetitorAnalysisContext(referenceImages),
          ].filter(Boolean).join('\n\n') || undefined;

          const [effectiveWidth, effectiveHeight] = getEffectiveImageSize().split('x').map(Number);
          let selectedStyleList: SelectedStyle[] = selectedStyles.length > 0
            ? selectedStyles
            : selectedStyle
            ? [selectedStyle]
            : [];

          // 自動で選ぶときは、事前に選んでおいたスタイルをそのまま使う
          if (selectedStyleList.length === 0 && effectiveStyleMode !== 'manual' && stylePreview.styles.length > 0) {
            selectedStyleList = await resolveStylePrompts(stylePreview.styles.slice(0, imageCount));
            lastAutoStyleIdsRef.current = selectedStyleList.map((style) => style.id);
          }

          // Auto-style: the checkbox clears any manual selection, so when it
          // is on selectedStyleList is empty here. Pull product-matched
          // recommendations and pick a distinct style per banner; the picked
          // styles feed the same styleAllocations distribution as a manual
          // multi-select, so each style becomes its own server-side job.
          if (autoStyleEnabled && selectedStyleList.length === 0) {
            let autoResp: AutoStyleResponse | null = null;
            try {
              autoResp = await fetchAutoStyles({
                productId: selectedProductData.product.id,
                inputText: prompt || undefined,
                limit: getDiverseStyleCandidateLimit(imageCount),
              });
              if (autoResp?.product_fetching) {
                // 取得中に選んだスタイルでは候補が減る。スタイルなしで生成せず、終わるまで待ってもらう
                setError(autoResp.reason);
                return;
              }
              selectedStyleList = pickRandomDistinctStyles(
                autoResp?.recommended ?? [],
                imageCount,
                lastAutoStyleIdsRef.current,
              ).map(recommendedStyleToSelected);
              lastAutoStyleIdsRef.current = selectedStyleList.map((style) => style.id);
            } catch (e) {
              console.warn('Auto style selection failed:', e);
            }
            if (selectedStyleList.length === 0) {
              console.warn(
                'Auto style enabled but no candidates were returned; generating without a style.',
                autoResp?.no_eligible_style_reason,
              );
              if (autoResp?.no_eligible_style_reason) {
                setError(autoResp.no_eligible_style_reason);
              }
            }
          }

          if (selectedStyleList.length > imageCount) {
            setError(`生成枚数は選択したスタイル数（${selectedStyleList.length}件）以上にしてください`);
            return;
          }

          const styleAllocations: Array<{ style: SelectedStyle | null; count: number }> =
            selectedStyleList.length > 0
              ? selectedStyleList.map(style => ({ style, count: 1 }))
              : [{ style: null, count: imageCount }];

          if (selectedStyleList.length > 0) {
            for (let i = selectedStyleList.length; i < imageCount; i++) {
              const randomStyle = selectedStyleList[Math.floor(Math.random() * selectedStyleList.length)];
              const allocation = styleAllocations.find(item => item.style?.id === randomStyle.id);
              if (allocation) allocation.count += 1;
            }
          }

          let allSuccess = true;
          const errors: string[] = [];

          for (const allocation of styleAllocations) {
            const styleForJob = allocation.style;
            // Respect each style's max_material_count: trim "material" refs to
            // the head of the user-selected order. Non-material refs pass
            // through unchanged. The same trim must be applied to the
            // prompt-generation metadata, otherwise the server-side LLM
            // describes images that are no longer in the payload.
            const styleConstraint = styleForJob
              ? { max_material_count: styleForJob.maxMaterialCount ?? null }
              : null;
            const refsForAllocation = filterMaterialsByStyle(
              allRefImages,
              styleConstraint,
            );
            const metadataForAllocation = filterMaterialsByStyle(
              referenceImagesMetadata,
              styleConstraint,
              (m) => m.category,
            );
            const response = await imageGenerationService.generateAsync(
              '', // Prompt will be generated server-side in the job
              allocation.count,
              refsForAllocation.length > 0 ? refsForAllocation : undefined,
              effectiveWidth,
              effectiveHeight,
              'product',
              {
                product_id: selectedProductData.product.id,
                style_id: styleForJob?.id,
                prompt_generation_params: {
                  additional_context: combinedAdditionalContext,
                  reference_images: metadataForAllocation.length > 0 ? metadataForAllocation : undefined,
                  target_segment: selectedProductData.segmentName || undefined,
                  appeal_axis: appealAxisText,
                  include_person: includePersonApi,
                  format_name: styleForJob?.name,
                  format_cta: styleForJob?.cta,
                  format_feature: styleForJob?.feature,
                  style_prompt: styleForJob?.stylePrompt,
                  aspect_ratio: aspectRatioApi,
                  textless_mode: textlessMode,
                },
              },
              'banner_ads',
              bannerImageModel,
              proMode
            );

            if (!response.success) {
              allSuccess = false;
              errors.push(`${styleForJob?.name || 'スタイルなし'}: ${response.message || 'ジョブの開始に失敗しました'}`);
            }
          }

          if (allSuccess) {
            setStyleVariationSource(null);
            setJobsRefreshTrigger(prev => prev + 1);
            showToast('バナーの生成を開始しました');
          } else if (errors.length > 0) {
            setError(`一部のジョブでエラーが発生しました: ${errors.join(', ')}`);
          }
        } catch (promptErr: any) {
          console.error('Error starting product mode generation:', promptErr);
          if (promptErr?.response?.status === 402 || promptErr?.response?.data?.error === 'credit_insufficient') {
            setShowUpgradeCta(true);
          } else {
            setShowUpgradeCta(false);
          }
          setError(promptErr?.response?.data?.message || promptErr?.message || 'ジョブの開始に失敗しました');
        }
      } else {
        // Preserve every manual selection, including when switching from product mode.
        const stylesForFreetext = selectedStyles.length > 0
          ? selectedStyles
          : selectedStyle ? [selectedStyle]
          : effectiveStyleMode === 'none'
            ? await resolveStylePrompts(stylePreview.styles.length > 0
              ? stylePreview.styles.slice(0, imageCount)
              : pickRandomDistinctStyles(await loadAllStyles().catch(() => []), imageCount))
            : [];
        const styleAllocations: Array<{ style: SelectedStyle | null; count: number }> = stylesForFreetext.length > 0
          ? stylesForFreetext.map((style, index) => ({
              style,
              count: Math.floor(imageCount / stylesForFreetext.length) + (index < imageCount % stylesForFreetext.length ? 1 : 0),
            }))
          : [{ style: null, count: imageCount }];
        const competitorAnalysisContext = buildCompetitorAnalysisContext(referenceImages);
        const errors: string[] = [];

        for (const allocation of styleAllocations) {
          const styleForJob = allocation.style;
          let basePrompt = `画像サイズ: ${sizeLabel}\n\n`;
          if (styleForJob) {
            basePrompt += `スタイル: ${styleForJob.name}\n${styleForJob.stylePrompt}\n\n`;
          }
          if (textlessMode) {
            basePrompt += `【重要】画像内にテキストや文字を含めないでください。文字を一切生成しないでください。\n\n`;
          }
          if (competitorAnalysisContext) {
            basePrompt += `【競合参考情報】\n${competitorAnalysisContext}\n\n`;
          }
          basePrompt += prompt;

          const refsForFreetext = filterMaterialsByStyle(
            allRefImages,
            styleForJob ? { max_material_count: styleForJob.maxMaterialCount ?? null } : null,
          );
          const styleVariationMetadata = styleVariationSource
            ? {
                style_variation: true,
                source_image_id: styleVariationSource.sourceImageId,
                style_id: styleForJob?.id,
                style_name: styleForJob?.name,
              }
            : undefined;

          // Keep the allocation together so server-side variation indices cover every image.
          const response = await imageGenerationService.generateAsync(
            basePrompt,
            allocation.count,
            refsForFreetext.length > 0 ? refsForFreetext : undefined,
            width,
            height,
            'freetext',
            styleForJob ? { style_id: styleForJob.id } : undefined,
            'banner_ads',
            bannerImageModel,
            proMode,
            styleVariationMetadata
          );

          if (!response.success) {
            errors.push(`${styleForJob?.name || 'スタイルなし'}: ${response.message || 'ジョブの開始に失敗しました'}`);
          }
        }
        if (errors.length === 0) {
          setStyleVariationSource(null);
          setJobsRefreshTrigger(prev => prev + 1);
          showToast('バナーの生成を開始しました');
        } else {
          setError(`一部のジョブでエラーが発生しました: ${errors.join(', ')}`);
        }
      }
    } catch (err: any) {
      console.error('Error starting image generation:', err);
      if (err?.response?.status === 402 || err?.response?.data?.error === 'credit_insufficient') {
        setShowUpgradeCta(true);
      } else {
        setShowUpgradeCta(false);
      }
      setError(err.response?.data?.message || '画像生成ジョブの開始中にエラーが発生しました');
    } finally {
      setIsStartingGeneration(false);
    }
  };

  const handleJobCompleted = async () => {
    // Retry fetching with delay to handle DB write timing
    const requestKey = historyFilterKey;
    const fetchNewImages = async () => {
      // 取得中に一覧を1ページ目から取り直したら（絞り込み中にタグを外したときなど）、この応答は古いので捨てる
      const reloadToken = historyReloadTokenRef.current;
      const isStale = () => historyFilterKeyRef.current !== requestKey || historyReloadTokenRef.current !== reloadToken;
      try {
        const response = await imageGenerationService.getImageHistory(1, 'banner_ads', historyFilters);
        if (isStale()) return;
        if (response.success) {
          const fetchedImages = response.data.data;
          setDisplayedContent(prev => {
            const existingIds = new Set(prev.map(img => img.id));
            const newImages = fetchedImages.filter(img => !existingIds.has(img.id));
            if (newImages.length > 0) {
              return [...newImages, ...prev];
            }
            return prev;
          });
          setTotalPages(response.data.last_page);
          setHasMore(1 < response.data.last_page);

          // Also refresh organization view if active
          if (historyView === 'organization') {
            const orgResponse = await imageGenerationService.getOrganizationImageHistory(1, 'banner_ads', historyFilters);
            if (isStale()) return;
            if (orgResponse.success) {
              setOrganizationImages(prev => {
                const existingIds = new Set(prev.map(img => img.id));
                const newImages = orgResponse.data.data.filter(img => !existingIds.has(img.id));
                if (newImages.length > 0) {
                  return [...newImages, ...prev];
                }
                return prev;
              });
            }
          }
        }
      } catch (err) {
        console.error('Failed to check for new images:', err);
      }
    };

    const claimGenerateBonus = async (): Promise<boolean> => {
      try {
        const response = await onboardingV2Service.claim();
        if (response.granted_now.generate) {
          setCelebrationActive(true);
          return true;
        }
      } catch (err) {
        console.error('Failed to claim onboarding generation bonus:', err);
      }

      return false;
    };

    // Fetch immediately
    await fetchNewImages();
    const generatedBonusClaimed = await claimGenerateBonus();
    // Retry after 2 seconds to catch images that may not be in DB yet
    setTimeout(() => {
      void fetchNewImages();
      if (!generatedBonusClaimed) {
        void claimGenerateBonus();
      }
    }, 2000);
  };

  const handleRegenerateJob = async (job: any) => {
    try {
      setError(null);

      // productモードのジョブは一覧APIに product_id が含まれないため、
      // サーバー側で生成済みのプロンプトを使い freetext として再生成する。
      // プロンプト生成前に失敗したジョブはプロンプトが空なので再生成不可。
      if (job.input_mode === 'product' && !job.prompt) {
        setError('プロンプト生成前に失敗したジョブは再生成できません。商品を選択して再度生成してください。');
        return;
      }

      const refImages = job.reference_images?.map((img: any) => ({
        type: (img.category === 'brand_logo' ? 'brand_logo' :
               img.category === 'product_image' ? 'product_image' :
               'material') as 'brand_logo' | 'product_image' | 'material',
        s3_key: img.s3_key,
        image_data: img.image_data,
      }));

      const response = await imageGenerationService.generateAsync(
        job.prompt,
        job.count,
        refImages,
        job.target_width,
        job.target_height,
        'freetext',
        undefined,
        'banner_ads',
        bannerImageModel,
        proMode
      );

      if (response.success) {
        setJobsRefreshTrigger(prev => prev + 1);
      } else {
        setError(response.message || '再生成に失敗しました');
      }
    } catch (err: any) {
      console.error('Error regenerating job:', err);
      setError(err.response?.data?.message || '再生成中にエラーが発生しました');
    }
  };

  /**
   * 商品の全ブランドロゴ候補を取得する（productId 単位でキャッシュ）。
   * 呼び出し側が product_images をすでに持っている場合は logoCandidatesCacheRef を
   * 事前にプライムしておくことでネットワーク往復を避けられる。
   */
  const getBrandLogoCandidates = useCallback(async (productId: number): Promise<LogoCandidateEntry[]> => {
    const cached = logoCandidatesCacheRef.current.get(productId);
    if (cached) return cached;

    try {
      const response = await productImageService.getProductImages(productId, 'brand_logo');
      const logos: LogoCandidateEntry[] = response.data.map(img => ({
        id: img.id,
        url: img.url ?? '',
        file_path: img.file_path,
        file_name: img.file_name,
        is_primary: img.is_primary,
        sort_order: img.sort_order,
        logo_tone: img.logo_tone ?? null,
      }));
      logoCandidatesCacheRef.current.set(productId, logos);
      return logos;
    } catch (err) {
      console.error('ブランドロゴ候補の取得に失敗しました:', err);
      return [];
    }
  }, []);

  /**
   * 選択中のスタイルの背景トーンに対し、参照画像内の brand_logo が視認しにくい
   * トーンだった場合、同じ商品の別バリエーションに自動で差し替える。
   * 差し替えを行った場合のみ Toast を表示し、以降そのスタイルに対しては
   * （ユーザーの再選択を最終決定として尊重するため）自動差し替えを止める。
   */
  const applyLogoToneAutoSelect = useCallback(async (
    images: SelectedProductImage[],
    backgroundTone: BackgroundTone | null | undefined,
  ): Promise<SelectedProductImage[]> => {
    if (!backgroundTone || images.length === 0) return images;

    const nextImages = [...images];
    let swappedAny = false;
    let lastReplacementTone: LogoTone | null = null;

    for (let i = 0; i < nextImages.length; i++) {
      const img = nextImages[i];
      if (img.category !== 'brand_logo') continue;
      if (!isLogoToneConflicting(img.logoTone, backgroundTone)) continue;

      const candidates = await getBrandLogoCandidates(img.productId);
      const replacement = selectLogoForBackground(candidates, backgroundTone);
      if (!replacement || replacement.id === img.id) continue;

      nextImages[i] = {
        ...img,
        id: replacement.id,
        url: replacement.url,
        s3Key: replacement.file_path,
        fileName: replacement.file_name,
        logoTone: replacement.logo_tone ?? null,
      };
      swappedAny = true;
      lastReplacementTone = replacement.logo_tone ?? null;
    }

    if (!swappedAny) return images;

    showToast(buildLogoSwapMessage(backgroundTone, lastReplacementTone), 'info');
    setLogoAutoSwapLocked(true);
    return nextImages;
  }, [getBrandLogoCandidates, showToast]);

  const handleProductSelectionConfirm = async (data: ProductSelectionData) => {
    setSelectedProductData(data);
    setShowProductSelectionModal(false);

    if (data.textlessMode !== undefined) {
      setTextlessMode(data.textlessMode);
    }

    if (data.selectedImages && data.selectedImages.length > 0) {
      const convertedImages: SelectedProductImage[] = data.selectedImages.map(img => ({
        id: img.id,
        url: img.url,
        s3Key: img.file_path,
        category: img.category,
        productId: img.product_id,
        productName: data.product.name,
        fileName: img.file_name,
        logoTone: img.logo_tone ?? null,
      }));

      // すでに持っている商品の全画像リストでロゴ候補キャッシュを事前にプライムしておく
      // （getBrandLogoCandidates のネットワーク取得を避けるため）。
      if (data.product.product_images) {
        const logos: LogoCandidateEntry[] = data.product.product_images
          .filter(img => img.category === 'brand_logo')
          .map(img => ({
            id: img.id,
            url: img.url,
            file_path: img.file_path,
            file_name: img.file_name,
            is_primary: img.is_primary,
            sort_order: img.sort_order,
            logo_tone: img.logo_tone ?? null,
          }));
        logoCandidatesCacheRef.current.set(data.product.id, logos);
      }

      if (logoAutoSwapLocked) {
        setReferenceImages(convertedImages);
      } else {
        const adjusted = await applyLogoToneAutoSelect(convertedImages, selectedStyle?.backgroundTone);
        setReferenceImages(adjusted);
      }
    } else {
      // 画像を選ばずに確定したら、前の商品の参照画像を新しい商品の生成へ持ち越さない
      setReferenceImages([]);
    }
  };

  const handleReferenceImageSelectionConfirm = useCallback(async (selected: SelectedProductImage[]) => {
    setShowImageSelectionModal(false);

    if (logoAutoSwapLocked) {
      setReferenceImages(selected);
      return;
    }

    const adjusted = await applyLogoToneAutoSelect(selected, selectedStyle?.backgroundTone);
    setReferenceImages(adjusted);
  }, [applyLogoToneAutoSelect, logoAutoSwapLocked, selectedStyle]);

  const handleStyleSelect = useCallback((style: SelectedStyle | null) => {
    setStyleMode(style ? 'manual' : 'none');
    setSelectedStyle(style);
    setSelectedStyles(style ? [style] : []);
    setLogoAutoSwapLocked(false);
    void applyLogoToneAutoSelect(referenceImages, style?.backgroundTone).then(adjusted => {
      setReferenceImages(adjusted);
    });
  }, [applyLogoToneAutoSelect, referenceImages]);

  const handleStyleSelectMany = useCallback((styles: SelectedStyle[]) => {
    setStyleMode(styles.length ? 'manual' : 'none');
    setSelectedStyles(styles);
    const primaryStyle = styles[0] || null;
    setSelectedStyle(primaryStyle);
    setLogoAutoSwapLocked(false);
    void applyLogoToneAutoSelect(referenceImages, primaryStyle?.backgroundTone).then(adjusted => {
      setReferenceImages(adjusted);
    });
  }, [applyLogoToneAutoSelect, referenceImages]);

  const handleBannerSelect = useCallback((selectedBanners: any[]) => {
    const bannerImages = selectedBanners.map((banner) => ({
      id: `banner-${banner.id}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      preview: banner.url,
      base64: '',
      s3Key: banner.s3Key,
      category: 'material' as const,
    }));
    if (bannerImages.length > 0) {
      setUploadedReferenceImages(prev => [...prev, ...bannerImages]);
    }
  }, []);

  const handleReferenceImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const newImages: typeof uploadedReferenceImages = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (!file.type.startsWith('image/')) continue;
      if (file.size > 10 * 1024 * 1024) {
        showToast(`${file.name} は10MBを超えているためスキップされました`, 'error');
        continue;
      }

      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result as string;
          resolve(result.split(',')[1]);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      const preview = URL.createObjectURL(file);
      newImages.push({
        id: `upload-${Date.now()}-${i}`,
        file,
        preview,
        base64,
        category: 'material',
      });
    }

    setUploadedReferenceImages(prev => [...prev, ...newImages]);
    if (uploadInputRef.current) {
      uploadInputRef.current.value = '';
    }
  };

  const handleRemoveUploadedImage = (imageId: string) => {
    setUploadedReferenceImages(prev => {
      const imageToRemove = prev.find(img => img.id === imageId);
      if (imageToRemove) URL.revokeObjectURL(imageToRemove.preview);
      return prev.filter(img => img.id !== imageId);
    });
  };

  const handleUploadedImageCategoryChange = (imageId: string, category: 'brand_logo' | 'product_image' | 'material') => {
    setUploadedReferenceImages(prev =>
      prev.map(img => img.id === imageId ? { ...img, category } : img)
    );
  };

  const handleDownloadImage = async (id: number, url: string, prompt: string) => {
    try {
      const apiUrl = `${process.env.REACT_APP_API_URL}/api/images/${id}/download`;
      const token = localStorage.getItem('tasky_auth_token');
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
        },
      });
      if (!response.ok) throw new Error('Download API failed');
      const data = await response.json();
      if (data.download_url) {
        const link = document.createElement('a');
        link.href = data.download_url;
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        track(ANALYTICS_EVENTS.DOWNLOAD_COMPLETED, { type: 'banner', image_id: id });
      } else {
        throw new Error('No download URL in response');
      }
    } catch (err) {
      console.error('Download failed:', err);
      showToast('画像のダウンロードに失敗しました。しばらくしてからもう一度お試しください。', 'error');
    }
  };

  const handleToggleBulkSelect = (id: number) => {
    // Backend caps a single zip at 50 images
    if (!bulkSelectedIds.has(id) && bulkSelectedIds.size >= 50) {
      showToast('一度に選べるのは50枚までです');
      return;
    }
    setBulkSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  // 選択は state と ref の両方を進める。ref は上限判定を最新値で行うため（handleToggleAbSelect）。
  const updateAbSelection = (next: number[]) => {
    abSelectedIdsRef.current = next;
    setAbSelectedIds(next);
  };

  // ABテストモードの ON/OFF。切り替えのたびに候補は捨てる（前回の選択が残ったまま再開しない）。
  const handleToggleAbTestMode = () => {
    setAbTestMode(prev => !prev);
    updateAbSelection([]);
    setSelectMode(false);
    setSelectModeIds(new Set());
  };

  // 一括選択モードの ON/OFF。ABテストモードとは同時に入らない。抜けるときは選択を捨てる
  const handleToggleSelectMode = () => {
    setSelectMode(prev => !prev);
    setSelectModeIds(new Set());
    setAbTestMode(false);
    updateAbSelection([]);
  };

  const handleToggleModeSelect = (id: number) => {
    // タグをまとめて付ける API の上限（50枚）に合わせる
    if (!selectModeIds.has(id) && selectModeIds.size >= 50) {
      showToast('一度に選べるのは50枚までです');
      return;
    }
    setSelectModeIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleToggleAbSelect = (id: number) => {
    // 上限判定は ref の最新値で行う。レンダー時の state を読むと同じレンダーで2回呼ばれた場合に
    // 5枚目が入りうるが、ref は同期で進むので取りこぼさない。
    // （setAbSelectedIds(prev => …) 内で判定する案は、React が updater を同期実行する保証が
    //   なく、弾いた事実を呼び出し側で受け取れない＝トーストが出ないため採らない）
    const current = abSelectedIdsRef.current;
    if (current.includes(id)) {
      updateAbSelection(current.filter(selectedId => selectedId !== id));
      return;
    }
    if (current.length >= AB_TEST_MAX_IMAGES) {
      showToast(`ABテストは最大 ${AB_TEST_MAX_IMAGES} 枚までです`);
      return;
    }
    updateAbSelection([...current, id]);
  };

  const handleBulkDownload = async () => {
    if (bulkSelectedIds.size === 0 || isBulkDownloading) return;
    setIsBulkDownloading(true);
    try {
      const apiUrl = `${process.env.REACT_APP_API_URL}/api/images/download-zip`;
      const token = localStorage.getItem('tasky_auth_token');
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ids: Array.from(bulkSelectedIds) }),
      });
      if (!response.ok) {
        let message = '一括ダウンロードに失敗しました';
        try {
          const data = await response.json();
          if (data?.message) message = data.message;
        } catch { /* JSON でなければ既定メッセージ */ }
        setError(message);
        return;
      }
      const disposition = response.headers.get('Content-Disposition') || '';
      const match = disposition.match(/filename="?([^"]+)"?/);
      const fileName = match ? match[1] : `richika-banners-${Date.now()}.zip`;
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
      track(ANALYTICS_EVENTS.DOWNLOAD_COMPLETED, { type: 'banner_bulk', count: bulkSelectedIds.size });
      setBulkSelectedIds(new Set());
    } catch (err) {
      console.error('Bulk download failed:', err);
      setError('一括ダウンロードに失敗しました。しばらくしてからもう一度お試しください。');
    } finally {
      setIsBulkDownloading(false);
    }
  };

  const handleUseImageAsReference = async (image: GeneratedImage) => {
    try {
      const response = await imageGenerationService.getImageBase64(image.id);
      if (!response.success || !response.data) {
        throw new Error(response.message || 'Failed to get image data');
      }
      setInputMode('freetext');
      setUploadedReferenceImages(prev => [...prev, {
        id: `generated-${image.id}-${Date.now()}`,
        preview: response.data.url,
        base64: response.data.base64,
        // 元のキーを送ると、どの生成画像を元に作り直したかをジョブから辿れる
        // （base64 だけだと reference-images/ に別名で保存され、持ち込み画像と区別できない）
        s3Key: image.s3_key || undefined,
        category: 'material' as const,
        sourceImage: image,
      }]);
      setSelectedImage(null);
      setTimeout(() => {
        promptSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    } catch (err) {
      console.error('Error converting image to reference:', err);
      showToast('画像を参照画像として追加できませんでした', 'error');
    }
  };

  const handleGenerateStyleVariations = async (image: GeneratedImage) => {
    try {
      const response = await imageGenerationService.getImageBase64(image.id);
      if (!response.success || !response.data) {
        throw new Error(response.message || 'Failed to get image data');
      }

      setInputMode('freetext');
      setImageCount(3);
      setStyleMode('none');
      setStyleVariationSource({
        sourceImageId: image.id,
        styleId: image.style_id ?? undefined,
        styleName: image.style_name ?? undefined,
      });

      if (image.style_id && image.style_name) {
        const inheritedStyle: SelectedStyle = {
          id: image.style_id,
          name: image.style_name,
          stylePrompt: `${image.style_name}のデザイン傾向、配色、余白、構図、質感を維持する`,
          aspectRatio: '',
          category: '',
        };
        setStyleMode('manual');
        setSelectedStyle(inheritedStyle);
        setSelectedStyles([inheritedStyle]);
      } else {
        setSelectedStyle(null);
        setSelectedStyles([]);
      }

      setUploadedReferenceImages(prev => [...prev, {
        id: `style-variation-${image.id}-${Date.now()}`,
        preview: response.data.url,
        base64: response.data.base64,
        s3Key: image.s3_key,
        category: 'material' as const,
      }]);

      const styleNameLine = image.style_name
        ? `「${image.style_name}」スタイルの特徴を維持してください。`
        : '参照画像のデザインスタイルを維持してください。';
      setPrompt([
        styleNameLine,
        '参照画像の色使い、余白、構図、質感、文字の見せ方を踏襲しつつ、コピーやレイアウト違いの広告バナーを複数案生成してください。',
        '元画像の単純な複製ではなく、同じブランド感が伝わる別案にしてください。',
      ].join('\n'));

      setSelectedImage(null);
      setTimeout(() => {
        promptSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    } catch (err) {
      console.error('Error preparing style variations:', err);
      showToast('この画像のスタイルで複数案を作る準備に失敗しました', 'error');
    }
  };

  const handleDeleteImage = async (id: number) => {
    if (!(await confirm({ title: 'この画像を削除しますか？', tone: 'destructive', confirmText: '削除する' }))) return;
    try {
      const response = await imageGenerationService.deleteImage(id);
      if (response.success) {
        setDisplayedContent(prev => prev.filter(img => img.id !== id));
        if (selectedImage?.id === id) {
          setSelectedImage(null);
        }
      }
    } catch (err) {
      console.error('Error deleting image:', err);
      showToast('画像の削除に失敗しました', 'error');
    }
  };

  const handleCopyToPrompt = (text: string) => {
    setPrompt(prev => prev ? `${prev}\n\n${text}` : text);
  };

  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (container && container.scrollTop + container.clientHeight >= container.scrollHeight - 800) {
      if (historyView === 'my') {
        if (hasMore && !isLoading && !myHistoryLoadingRef.current) setCurrentPage(prev => prev + 1);
      } else {
        if (orgHasMore && !isOrgLoading && !orgHistoryLoadingRef.current) setOrgCurrentPage(prev => prev + 1);
      }
    }
  }, [hasMore, isLoading, orgHasMore, isOrgLoading, historyView]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll]);

  const handleToggleImageFavorite = async (image: GeneratedImage) => {
    try {
      const result = await imageGenerationService.toggleImageFavorite(image.id);
      const updateImage = (img: GeneratedImage) =>
        img.id === image.id ? { ...img, is_favorited: result.is_favorited } : img;

      setDisplayedContent(prev => prev.map(updateImage));
      setOrganizationImages(prev => {
        const updated = prev.map(updateImage);
        return historyView === 'favorites' && !result.is_favorited
          ? updated.filter(img => img.id !== image.id)
          : updated;
      });
      setSelectedImage(prev => prev?.id === image.id ? { ...prev, is_favorited: result.is_favorited } : prev);
    } catch (err) {
      console.error('Failed to toggle image favorite:', err);
      showToast('お気に入りの更新に失敗しました', 'error');
    }
  };

  const handleHistoryViewChange = (nextView: typeof historyView) => {
    setHistoryView(nextView);
    setBulkSelectedIds(new Set());
    setSelectModeIds(new Set());
    if (nextView === 'my') {
      setCurrentPage(1);
      setDisplayedContent([]);
    } else {
      setOrgCurrentPage(1);
      setOrganizationImages([]);
    }
  };

  const resetHistoryLists = () => {
    setCurrentPage(1);
    setOrgCurrentPage(1);
    setDisplayedContent([]);
    setOrganizationImages([]);
    setHistoryReloadToken(token => token + 1);
  };

  const handleHistorySortChange = (sort: ImageHistorySort) => {
    setHistorySort(sort);
    // 下のバーは一覧に出ている選択だけに付けるので、取り直して外れたバナーを選んだまま残さない
    setSelectModeIds(new Set());
    resetHistoryLists();
  };

  const handleProductFilterChange = (next: number | null) => {
    setProductFilter(next);
    setBulkSelectedIds(new Set());
    setSelectModeIds(new Set());
    resetHistoryLists();
  };

  const updateImageStyleRating = (imageId: number, rating: StyleRating | null) => {
    const update = (image: GeneratedImage): GeneratedImage =>
      image.id === imageId ? { ...image, style_rating: rating } : image;
    setDisplayedContent(prev => prev.map(update));
    setOrganizationImages(prev => prev.map(update));
    setSelectedImage(prev => (prev?.id === imageId ? { ...prev, style_rating: rating } : prev));
  };

  const historyViewTabs: TaskyTabItem<typeof historyView>[] = [
    { value: 'my', label: '自分の画像', icon: <User className="w-4 h-4" /> },
    { value: 'organization', label: '組織の画像', icon: <Users className="w-4 h-4" /> },
    {
      value: 'favorites',
      label: 'お気に入り',
      icon: <Heart className={`w-4 h-4 ${historyView === 'favorites' ? 'fill-current' : ''}`} />,
    },
  ];

  const currentContent = historyView === 'my' ? displayedContent : organizationImages;
  const currentLoading = historyView === 'my' ? isLoading : isOrgLoading;
  const isHistoryFiltered = tagFilterIds.length > 0 || productFilter !== null;
  // タブを跨いでも選択は維持されるので、今のタブに出ていない選択の枚数を案内に添える
  // （「選択済み」が1枚も見えないのに (2/4) と出る状態を避ける）。
  const hiddenAbSelectedCount = abSelectedIds.filter(
    id => !currentContent.some(image => image.id === id),
  ).length;
  const customWidthNumber = parseInt(customWidth) || 0;
  const customHeightNumber = parseInt(customHeight) || 0;
  const customDirection = getSizeDirection(customWidthNumber, customHeightNumber);
  const customRatio = getSimplifiedRatio(customWidthNumber, customHeightNumber);

  return (
    <ResponsiveAppShell
      sidebar={(
        <Sidebar
          collapsed={sidebarCollapsed}
          onToggleCollapse={setSidebarCollapsed}
        />
      )}
      mobileSidebar={(
        <Sidebar
          collapsed={false}
          onToggleCollapse={() => undefined}
        />
      )}
      topbarTitle="バナー広告"
    >
      <div className="flex min-h-[calc(100dvh-56px)] flex-col bg-white lg:h-screen lg:min-h-0">
        {/* Header */}
        <TaskyTopBar
          title="バナー広告"
          icon={<ImageIcon className="w-5 h-5" />}
          sidebarCollapsed={sidebarCollapsed}
          onExpandSidebar={() => setSidebarCollapsed(false)}
        />

        {/* Main Scrollable Content */}
        <div ref={scrollContainerRef} className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden">
          <TaskyPageContainer>
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm text-gray-500">商品を選ぶ → デザイン・サイズを決める → 生成した画像を保存</p>
              <button type="button" onClick={() => setManualGuideOpen(true)} className="inline-flex items-center gap-1 rounded-lg px-2 py-2 text-sm font-semibold text-primary hover:bg-primary/5">
                <HelpCircle className="h-4 w-4" />この画面の使い方
              </button>
            </div>
            {/* Generation Form */}
            <div ref={promptSectionRef} className="bg-gray-50 rounded-xl p-4 mb-4">
              <TaapBannerHandoff onApply={(data) => {
                setSelectedProductData(data);
                setInputMode('product');
                setAdditionalInstructions('');
                setReferenceImages([]);
                setUploadedReferenceImages([]);
                setImageCount(1);
                setError(null);
                setBannerGuideDismissed(true);
              }} />
              <div className="space-y-4">
                {/* Input Mode Tabs */}
                <GenerationInputModeTabs
                  value={inputMode}
                  onChange={setInputMode}
                />

                {/* Product Selection Mode */}
                {inputMode === 'product' && (
                  <div className="bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 rounded-lg p-3">
                    {showProductSelectionModal ? (
                      <div className="flex items-center justify-center gap-3 py-4">
                        <svg className="animate-spin h-5 w-5 text-purple" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span className="text-sm text-purple font-medium">商品を選択中...</span>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex items-center gap-2">
                            <FileText className="w-4 h-4 text-orange-600 flex-shrink-0" />
                            <span className="text-sm font-medium text-gray-900">商品情報</span>
                            {selectedProductData && (
                              <span className="text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded-full">
                                {selectedProductData.product.name}
                              </span>
                            )}
                          </div>
                          <TaskyButton
                            type="button"
                            size="sm"
                            data-tutorial="ads-banner-product-select"
                            onClick={() => setShowProductSelectionModal(true)}
                            className="flex-shrink-0"
                          >
                            <Sparkles className="w-3.5 h-3.5" />
                            {selectedProductData ? '商品を変更' : '商品を選択'}
                          </TaskyButton>
                        </div>

                        {/* Additional Instructions */}
                        {selectedProductData && (
                          <div className="mt-3 pt-3 border-t border-orange-200">
                            <label className="block text-xs text-gray-600 mb-1">
                              詳細を指示する（任意）
                            </label>
                            <textarea
                              placeholder="例: 夏らしい爽やかな雰囲気で、若い女性が使用しているシーンにしてください"
                              value={additionalInstructions}
                              onChange={(e) => setAdditionalInstructions(e.target.value.slice(0, 500))}
                              maxLength={500}
                              rows={2}
                              className="w-full p-2 border border-warm-border rounded-md focus:ring-2 focus:ring-primary focus:border-primary resize-none text-sm bg-white"
                            />
                            <div className="flex justify-end mt-1">
                              <span className="text-xs text-gray-400">{additionalInstructions.length}/500</span>
                            </div>
                          </div>
                        )}

                        {/* Generated prompt preview (staging only) */}
                        {shouldShowPrompt() && selectedProductData && prompt && (
                          <div className="mt-3 pt-3 border-t border-orange-200">
                            <p className="text-xs text-gray-500 mb-1">生成されるプロンプト:</p>
                            <pre className="text-xs text-gray-700 bg-white/50 p-2 rounded whitespace-pre-wrap max-h-32 overflow-y-auto">{prompt}</pre>
                          </div>
                        )}

                        {/* Product Selection Reference Images Preview */}
                        {referenceImages.length > 0 && (
                          <div className="mt-3 pt-3 border-t border-orange-200">
                            <p className="text-xs text-gray-500 mb-2">商品画像（参照画像として使用）:</p>
                            <div className="flex flex-wrap gap-2">
                              {referenceImages.map((img) => (
                                <div key={img.id} className="relative group w-16 h-16 rounded-lg overflow-hidden border-2 border-purple/30">
                                  <img src={img.url} alt={img.fileName || '商品画像'} className="w-full h-full object-contain bg-gray-100" />
                                  <button
                                    type="button"
                                    onClick={() => setReferenceImages(prev => prev.filter(i => i.id !== img.id))}
                                    className="absolute top-0.5 right-0.5 w-4 h-4 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                  >
                                    <X className="w-2.5 h-2.5" />
                                  </button>
                                  <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-60 text-white text-[10px] px-1 py-0.5 truncate">
                                    {img.category === 'brand_logo' ? 'ロゴ' : img.category === 'product_image' ? '商品' : '素材'}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}

                {/* Free Text Mode */}
                {inputMode === 'freetext' && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label htmlFor="banner-prompt" className="block text-sm font-medium text-gray-700">作りたい広告の内容</label>
                      <button
                        type="button"
                        onClick={() => setShowProductInfoModal(true)}
                        className="flex items-center gap-1.5 px-3 py-1 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded-md hover:bg-green-100 transition-colors"
                      >
                        <Copy className="w-3 h-3" />
                        商品情報をコピー
                      </button>
                    </div>
                    <textarea
                      id="banner-prompt"
                      aria-describedby="banner-prompt-help"
                      placeholder="商品名：○○ / 誰向け：○○な人 / 伝えたい魅力：○○ / 画像に載せる文言：○○"
                      value={prompt}
                      onChange={(e) => setPrompt(e.target.value.slice(0, 32000))}
                      maxLength={32000}
                      rows={4}
                      className="w-full p-3 border border-warm-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary resize-none text-sm"
                    />
                    <div id="banner-prompt-help" className="mt-2 flex flex-wrap items-center gap-2 text-xs text-gray-500">
                      <span>商品名・誰向け・魅力・載せたい文言を書くと、希望を伝えやすくなります。</span>
                      {!prompt.trim() && <button type="button" className="rounded border border-warm-border bg-white px-2 py-1 font-semibold text-primary" onClick={() => { setPrompt(BANNER_PROMPT_TEMPLATE); document.getElementById('banner-prompt')?.focus(); }}>入力例を使う</button>}
                    </div>
                    {showTouchUpHint && touchUpIntent && (
                      <TouchUpHint intent={touchUpIntent} onOpen={handleOpenTouchUpTool} onDismiss={handleDismissTouchUpHint} />
                    )}
                    <div className="flex justify-between items-center mt-1">
                      <span className="text-xs text-gray-500">{prompt.length}/32000</span>
                      <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={textlessMode}
                          onChange={(e) => setTextlessMode(e.target.checked)}
                          className="rounded border-warm-border text-primary focus:ring-primary"
                        />
                        テキストなしで生成
                        <span className="text-xs text-gray-400">（画像内に文字を含めない）</span>
                      </label>
                    </div>

                    {/* Sample Prompts - banner only */}
                    {bannerPrompts.length > 0 && (
                      <div className="mt-3">
                        <label className="block text-xs font-medium text-gray-500 mb-1.5">広告の入力例</label>
                        <div className="flex flex-wrap gap-2">
                          {bannerPrompts.map((sp) => (
                            <button
                              key={sp.id}
                              type="button"
                              onClick={() => setPrompt(sp.prompt_text)}
                              className="px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-full hover:bg-blue-100 transition-colors"
                            >
                              {sp.name}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <div data-tutorial="ads-banner-style">
                  <BannerStyleChoice
                    productMode={inputMode === 'product'}
                    mode={effectiveStyleMode}
                    names={selectedStyles.map(style => style.name)}
                    onSelect={() => setShowStyleSelectModal(true)}
                    onChange={(mode) => {
                      setStyleMode(mode);
                      if (mode !== 'manual') {
                        setSelectedStyle(null);
                        setSelectedStyles([]);
                        setLogoAutoSwapLocked(false);
                      }
                    }}
                  />
                </div>

                {/* Reference Images Section */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">参考画像（任意）</label>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => setShowImageSelectionModal(true)}
                      className="flex items-center gap-2 px-4 py-2 border border-warm-border rounded-md hover:bg-gray-50 transition-colors"
                    >
                      <FolderOpen className="w-4 h-4" />
                      商品画像から選択
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowBannerLibraryModal(true)}
                      className="flex items-center gap-2 px-4 py-2 border border-blue-300 bg-blue-50 rounded-md hover:bg-blue-100 transition-colors text-blue-700"
                    >
                      <ImageIcon className="w-4 h-4" />
                      自社バナーから選択
                    </button>
                    <button
                      type="button"
                      onClick={() => uploadInputRef.current?.click()}
                      className="flex items-center gap-2 px-4 py-2 border border-warm-border rounded-md hover:bg-gray-50 transition-colors"
                    >
                      <Upload className="w-4 h-4" />
                      アップロード
                    </button>
                    <input
                      ref={uploadInputRef}
                      type="file"
                      accept="image/*,.svg,image/svg+xml"
                      multiple
                      onChange={handleReferenceImageUpload}
                      className="hidden"
                    />
                    {(referenceImages.length > 0 || uploadedReferenceImages.length > 0) && (
                      <span className="text-sm text-gray-600">
                        {referenceImages.length + uploadedReferenceImages.length}枚選択中
                      </span>
                    )}
                  </div>

                  {/* Reference Images Preview */}
                  {(referenceImages.length > 0 || uploadedReferenceImages.length > 0) && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {referenceImages.map((img) => (
                        <div key={img.id} className="relative group w-20 h-20 rounded-lg overflow-hidden border-2 border-purple">
                          <img src={img.url} alt={img.fileName || '商品画像'} className="w-full h-full object-contain bg-gray-100" />
                          <button
                            type="button"
                            onClick={() => setReferenceImages(prev => prev.filter(i => i.id !== img.id))}
                            className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X className="w-3 h-3" />
                          </button>
                          <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-70 text-white text-xs px-1 py-0.5 truncate">
                            {img.category === 'brand_logo' ? 'ロゴ' : img.category === 'product_image' ? '商品' : '素材'}
                          </div>
                        </div>
                      ))}
                      {uploadedReferenceImages.map((img) => (
                        <div key={img.id} className="relative group w-20 h-20 rounded-lg overflow-hidden border-2 border-green-500">
                          <img src={img.preview} alt={img.file?.name || '参照画像'} className="w-full h-full object-contain bg-gray-100" />
                          <button
                            type="button"
                            onClick={() => handleRemoveUploadedImage(img.id)}
                            className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X className="w-3 h-3" />
                          </button>
                          <select
                            value={img.category}
                            onChange={(e) => handleUploadedImageCategoryChange(img.id, e.target.value as 'brand_logo' | 'product_image' | 'material')}
                            onClick={(e) => e.stopPropagation()}
                            className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-70 text-white text-xs px-0.5 py-0.5 border-none outline-none cursor-pointer"
                          >
                            <option value="brand_logo">ロゴ</option>
                            <option value="product_image">商品</option>
                            <option value="material">素材</option>
                          </select>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Size, Count, Generate Button */}
                <div className="space-y-4">
                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700">画像サイズ</label>
                    <div className="grid grid-cols-2 gap-2 lg:grid-cols-5">
                      {IMAGE_SIZE_PRESETS.map((preset) => {
                        const selected = !useCustomSize && imageSize === preset.value;
                        return (
                          <button
                            key={preset.value}
                            type="button"
                            onClick={() => {
                              setUseCustomSize(false);
                              setImageSize(preset.value);
                            }}
                            className={`flex min-h-[116px] flex-col justify-between rounded-lg border p-3 text-left transition-all ${
                              selected
                                ? 'border-primary bg-primary/5 shadow-sm ring-2 ring-primary/15'
                                : 'border-warm-border bg-white hover:border-primary/40 hover:bg-warm-hover'
                            }`}
                            aria-pressed={selected}
                          >
                            <span className="flex items-start justify-between gap-2">
                              <span>
                                <span className="block text-sm font-semibold text-dark">{preset.ratio}</span>
                                <span className="mt-0.5 block text-xs text-gray-500">{preset.label}</span>
                              </span>
                              <span className="flex h-10 w-14 items-center justify-center rounded-md bg-warm-surface">
                                <span className={`${preset.previewClass} rounded-sm border-2 border-primary bg-white`} />
                              </span>
                            </span>
                            <span>
                              <span className="block text-xs text-gray-500">{preset.dimensions}</span>
                              <span className="mt-1 block text-xs leading-snug text-gray-600">{preset.useCase}</span>
                            </span>
                          </button>
                        );
                      })}

                      <button
                        type="button"
                        onClick={() => setUseCustomSize(true)}
                        className={`flex min-h-[116px] flex-col justify-between rounded-lg border p-3 text-left transition-all ${
                          useCustomSize
                            ? 'border-primary bg-primary/5 shadow-sm ring-2 ring-primary/15'
                            : 'border-warm-border bg-white hover:border-primary/40 hover:bg-warm-hover'
                        }`}
                        aria-pressed={useCustomSize}
                      >
                        <span className="flex items-start justify-between gap-2">
                          <span>
                            <span className="block text-sm font-semibold text-dark">Custom</span>
                            <span className="mt-0.5 block text-xs text-gray-500">{customDirection}</span>
                          </span>
                          <span className="flex h-10 w-14 items-center justify-center rounded-md bg-warm-surface">
                            <span className="h-7 w-10 rounded-sm border-2 border-dashed border-primary bg-white" />
                          </span>
                        </span>
                        <span>
                          <span className="block text-xs text-gray-500">{customRatio}</span>
                          <span className="mt-1 block text-xs leading-snug text-gray-600">任意サイズ</span>
                        </span>
                      </button>
                    </div>
                  </div>

                  {useCustomSize && (
                    <div className="rounded-lg border border-warm-border bg-warm-surface p-3">
                      <label className="block text-sm font-medium text-gray-700 mb-2">カスタムサイズ</label>
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            id="banner-custom-width"
                        value={customWidth}
                            onChange={(e) => setCustomWidth(e.target.value)}
                            min={CUSTOM_IMAGE_SIZE_MIN}
                            max={CUSTOM_IMAGE_SIZE_MAX}
                            step={1}
                            aria-invalid={customSizeError !== null}
                            className={`h-10 w-24 rounded-md border bg-white px-2 text-center text-sm ${customSizeError ? 'border-red-400' : 'border-warm-border'}`}
                            placeholder="横幅"
                          />
                          <span className="text-gray-500 text-sm">x</span>
                          <input
                            type="number"
                            value={customHeight}
                            onChange={(e) => setCustomHeight(e.target.value)}
                            min={CUSTOM_IMAGE_SIZE_MIN}
                            max={CUSTOM_IMAGE_SIZE_MAX}
                            step={1}
                            aria-invalid={customSizeError !== null}
                            className={`h-10 w-24 rounded-md border bg-white px-2 text-center text-sm ${customSizeError ? 'border-red-400' : 'border-warm-border'}`}
                            placeholder="縦幅"
                          />
                        </div>
                        <div className="text-sm text-gray-600">
                          <span className="font-medium text-dark">{customDirection}</span>
                          <span className="mx-2 text-gray-300">/</span>
                          <span>{customRatio}</span>
                        </div>
                      </div>
                      {customSizeError ? (
                        <p className="mt-2 text-xs text-red-600">{customSizeError}</p>
                      ) : (
                        <p className="mt-2 text-xs text-gray-500">
                          {CUSTOM_IMAGE_SIZE_HINT}
                        </p>
                      )}
                    </div>
                  )}

                  <div>
                    <label htmlFor="banner-image-model" className="block text-sm font-medium text-gray-700 mb-1">AIモデル</label>
                    <div className="sm:max-w-xs">
                    <TaskySelect
                      id="banner-image-model"
                      disabled={isStartingGeneration}
                      value={bannerImageModel}
                      onChange={(e) => setBannerImageModel(e.target.value as BannerImageModel)}
                      className="text-sm"
                    >
                      {BANNER_IMAGE_MODEL_OPTIONS.map(option => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </TaskySelect>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="banner-image-count" className="block text-sm font-medium text-gray-700 mb-1">生成枚数</label>
                    <div className="sm:max-w-[220px]">
                    <TaskySelect
                      id="banner-image-count"
                      disabled={isStartingGeneration || imageCount === 0}
                      value={imageCount}
                      onChange={(e) => setImageCount(Number(e.target.value))}
                      className="text-sm"
                    >
                      {imageCount === 0 && <option value={0}>{concurrencyLimits ? '空き枠がありません' : '生成上限を確認中…'}</option>}
                      {imageCountOptions.map(n => (
                        <option key={n} value={n}>{n}枚</option>
                      ))}
                    </TaskySelect>
                    </div>
                    {concurrencyMessage && (
                      <p className="mt-1.5 text-sm leading-relaxed text-gray-600">{concurrencyMessage}</p>
                    )}
                  </div>

                  <button
                    type="button"
                    onClick={() => setProMode(!proMode)}
                    className={`hidden items-center gap-2 px-3 py-2 rounded-lg border-2 transition-all text-sm select-none ${
                      proMode
                        ? 'border-amber-500 bg-amber-50 text-amber-700'
                        : 'border-warm-border bg-white text-gray-500 hover:border-warm-border'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={proMode}
                      readOnly
                      className="rounded border-warm-border text-amber-600 focus:ring-amber-500 pointer-events-none"
                    />
                    <span className="font-semibold">PRO</span>
                    <span className="text-xs opacity-70">高品質モデル</span>
                  </button>
                </div>

                <div id="banner-generation-help" aria-live="polite" className="rounded-lg border border-warm-border bg-white p-3 text-sm">
                  {readiness ? (
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <p className="text-gray-700">{readiness.message}</p>
                      {readiness.target && <button type="button" onClick={goToMissingField} className="rounded-lg px-2 py-1 font-semibold text-primary underline underline-offset-4">{readiness.target === 'product' ? '商品を選ぶ' : '入力・設定を確認する'}</button>}
                    </div>
                  ) : <p className="text-gray-700">{inputMode === 'product' ? selectedProductData?.product.name : '入力した内容'} ・ {formatImageSizeLabel(getEffectiveImageSize())} ・ {imageCount}枚で生成できます。</p>}
                </div>

                <TaskyButton
                  type="button"
                  size="lg"
                  data-tutorial="ads-banner-generate"
                  onClick={handleGenerateMultipleImages}
                  disabled={Boolean(readiness)}
                  isLoading={isStartingGeneration}
                  aria-describedby="banner-generation-help"
                  className="w-full font-semibold"
                >
                  {isStartingGeneration ? '生成中...' : imageCount > 0 ? <><Sparkles />{imageCount}枚生成</> : '生成待機中'}
                </TaskyButton>

                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-md p-3">
                    <p className="text-sm text-red-800">{error}</p>
                    {showUpgradeCta && (
                      <div className="mt-2 flex flex-col gap-2">
                        <button
                          onClick={() => navigate('/plans')}
                          className="self-start bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold py-1.5 px-3 rounded-md transition-colors"
                        >
                          プランをアップグレード
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Generating Jobs Section */}
            <GeneratingJobsSection onJobCompleted={handleJobCompleted} onRegenerate={handleRegenerateJob} generationSource="banner_ads" refreshTrigger={jobsRefreshTrigger} onLimitsChange={setConcurrencyLimits} />

            {/* History View Toggle（2段目はタグだけの行） */}
            <div data-tutorial="ads-banner-results" className="mb-4 space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex flex-wrap items-center gap-2">
                <TaskyTabs<typeof historyView>
                  variant="pill"
                  aria-label="表示する画像の範囲"
                  value={historyView}
                  onChange={handleHistoryViewChange}
                  items={historyViewTabs}
                />
                {/* モード中はカード左上の解除ボタンが出ないので、一括DLも隠す（解除できないまま
                    ABの選択と無関係な画像をダウンロードできてしまうため）。選択自体は保持する。 */}
                {bulkSelectedIds.size > 0 && !pickingMode && (
                  <TaskyButton
                    type="button"
                    size="sm"
                    onClick={handleBulkDownload}
                    isLoading={isBulkDownloading}
                  >
                    {isBulkDownloading ? (
                      '作成中...'
                    ) : (
                      <>
                        <Download className="w-4 h-4" />
                        一括ダウンロード ({bulkSelectedIds.size})
                      </>
                    )}
                  </TaskyButton>
                )}
                {SHOW_AB_TEST_MODE && organization?.enable_auto_banner_ab_test && (
                  <>
                    <TaskyButton
                      type="button"
                      size="sm"
                      variant={abTestMode ? 'default' : 'outline'}
                      aria-pressed={abTestMode}
                      onClick={handleToggleAbTestMode}
                      data-track="banner-ab-test-mode"
                    >
                      <FlaskConical className="w-4 h-4" />
                      {abTestMode ? 'ABテストモード終了' : 'ABテストモード'}
                    </TaskyButton>
                    {abTestMode && (
                      <>
                        <span className="text-sm text-gray-600">
                          バナーを 2〜{AB_TEST_MAX_IMAGES} 枚選んでください（{abSelectedIds.length}/{AB_TEST_MAX_IMAGES}）
                          {hiddenAbSelectedCount > 0 && `（他のタブで選択中 ${hiddenAbSelectedCount} 枚）`}
                        </span>
                        <TaskyButton
                          type="button"
                          size="sm"
                          onClick={() => setAbTestOpen(true)}
                          disabled={abSelectedIds.length < 2}
                          data-track="banner-ab-test-open"
                        >
                          <FlaskConical className="w-4 h-4" />
                          ABテストを開始 ({abSelectedIds.length})
                        </TaskyButton>
                      </>
                    )}
                  </>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {filterProducts.length > 0 && (
                  <div className="w-44">
                    <TaskySelect
                      aria-label="商品で絞り込み"
                      value={productFilter ?? ''}
                      onChange={(e) => handleProductFilterChange(e.target.value ? Number(e.target.value) : null)}
                      className="h-9 text-sm"
                    >
                      <option value="">すべての商品</option>
                      {filterProducts.map(product => (
                        <option key={product.id} value={product.id}>{product.name}</option>
                      ))}
                    </TaskySelect>
                  </div>
                )}
                <label className="flex items-center gap-2 text-sm text-gray-600">
                  <ArrowDownAZ className="w-4 h-4" />
                  <div className="w-40">
                    <TaskySelect
                      value={historySort}
                      onChange={(e) => handleHistorySortChange(e.target.value as ImageHistorySort)}
                      className="h-9 text-sm"
                    >
                      <option value="newest">新しい順</option>
                      <option value="product_name_asc">商品名 A-Z</option>
                      <option value="product_name_desc">商品名 Z-A</option>
                      <option value="product_only">商品情報ありのみ</option>
                    </TaskySelect>
                  </div>
                </label>
                {/* 一括選択。ABテストモードのように、カードを押すと選択になる */}
                {bannerTags.enabled && (
                  <TaskyCheckbox
                    label="選択する"
                    checked={selectMode}
                    onChange={handleToggleSelectMode}
                    wrapperClassName="ml-1"
                  />
                )}
              </div>
            </div>
            <ImageTagBar controller={bannerTags} />
            </div>

            {/* Image Gallery */}
            {currentContent.length === 0 && !currentLoading ? (
              <p className="text-center text-gray-500 py-8">
                {isHistoryFiltered
                  ? '条件に合うバナーはありません'
                  : historyView === 'favorites' ? 'お気に入りはまだありません' : 'まだバナーが生成されていません'}
              </p>
            ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 items-start">
              {/* CSS columns だと縦流し（列ごとに上→下）になり新着順が読み順にならないため、
                  行方向（左上→右下）に流れる grid を使う */}
              {currentContent.map((image) => {
                const abSelected = abSelectedIds.includes(image.id);
                const modeSelected = selectMode && selectModeIds.has(image.id);
                const picked = abTestMode ? abSelected : modeSelected;
                const bulkSelected = !pickingMode && bulkSelectedIds.has(image.id);
                // モード中はカードそのものが選択ボタンになる。プレビューは開かない。
                const openOrSelect = () => {
                  if (abTestMode) {
                    handleToggleAbSelect(image.id);
                    return;
                  }
                  if (selectMode) {
                    handleToggleModeSelect(image.id);
                    return;
                  }
                  setSelectedImage(image);
                  // 一覧から詳細を開いた時だけスタイル採点用に残す（同じ利用者・同じ画像はサーバーで1回）
                  void imageGenerationService.recordDetailOpened(image.id);
                };
                return (
                <div
                  key={image.id}
                  data-testid={`banner-card-${image.id}`}
                  role={pickingMode ? 'button' : undefined}
                  aria-pressed={pickingMode ? picked : undefined}
                  className={`bg-white border border-warm-border rounded-lg overflow-hidden hover:shadow-lg transition-all duration-200 cursor-pointer group ${
                    picked ? 'ring-2 ring-primary' : bulkSelected ? 'ring-2 ring-blue-600' : ''
                  }`}
                  onClick={openOrSelect}
                >
                  <div className="relative">
                    <ThrottledImage
                      src={image.thumbnail_url || image.url}
                      alt={generatedImageTitle(image)}
                    />
                    {picked && (
                      <div className="absolute inset-0 bg-black/50 flex flex-col items-center justify-center text-white">
                        <Check className="w-8 h-8" />
                        <span className="text-sm font-semibold">選択済み</span>
                      </div>
                    )}
                    {/* モード中は一括ダウンロードの選択ボタンとホバー操作を出さない（選択の意味が二重になるため） */}
                    {/* 未選択でも枠付きのチェック欄を出し、選択済みはカード全体を青枠で囲む。
                        以前は未選択が無地の白い四角で、選択してもカード側に変化がなく、
                        押せたか分からず連打されていた。タッチ端末（hover なし）では常に表示する。 */}
                    {!pickingMode && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); handleToggleBulkSelect(image.id); }}
                      className={`absolute top-2 left-2 h-8 w-8 p-0 rounded-md shadow-md flex items-center justify-center border-2 transition-opacity focus-visible:opacity-100 [@media(hover:none)]:opacity-100 ${
                        bulkSelected
                          ? 'bg-blue-600 border-blue-600 text-white hover:bg-blue-700'
                          : 'bg-white/90 border-gray-400 hover:bg-gray-100'
                      } ${
                        bulkSelectedIds.size > 0 ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                      }`}
                      title={bulkSelected ? '選択を解除' : 'バナーを選択'}
                      aria-label={bulkSelected ? '選択を解除' : 'バナーを選択'}
                      aria-pressed={bulkSelected}
                    >
                      {bulkSelected && <Check className="w-4 h-4" />}
                    </button>
                    )}
                    {!pickingMode && (
                    <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="flex gap-1">
                        <button
                          onClick={(e) => { e.stopPropagation(); handleToggleImageFavorite(image); }}
                          className={`h-8 w-8 p-0 rounded-md shadow-md flex items-center justify-center ${
                            image.is_favorited ? 'bg-rose-500 text-white hover:bg-rose-600' : 'bg-white hover:bg-gray-100'
                          }`}
                          title={image.is_favorited ? 'お気に入りを解除' : 'お気に入りに追加'}
                        >
                          <Heart className={`w-4 h-4 ${image.is_favorited ? 'fill-current' : ''}`} />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDownloadImage(image.id, image.url, image.prompt); }}
                          className="h-8 w-8 p-0 bg-white rounded-md shadow-md flex items-center justify-center hover:bg-gray-100"
                        >
                          <Download className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    )}
                  </div>
                  <div className="p-3">
                    <button type="button" aria-pressed={pickingMode ? picked : undefined} onClick={(event) => { event.stopPropagation(); openOrSelect(); }} className="mb-2 text-left text-sm font-semibold text-dark hover:text-primary focus-visible:outline-primary">{generatedImageTitle(image)}</button>
                    <ImageTagList controller={bannerTags} image={image} editable={!pickingMode} className="mb-2" />
                    {image.product_name && (
                      <div className="mb-2 text-xs text-gray-600 bg-gray-50 border border-warm-border rounded px-2 py-1 truncate">
                        {image.product_name}
                      </div>
                    )}
                    {image.style_id && (
                      <div className="mb-2 flex items-center justify-between gap-2">
                        <span className="truncate text-xs text-gray-500">{image.style_name ?? '選択スタイル'}</span>
                        <StyleFeedbackButtons
                          imageId={image.id}
                          styleId={image.style_id}
                          currentRating={image.style_rating ?? null}
                          source="generated_image_card"
                          onRated={(rating) => updateImageStyleRating(image.id, rating)}
                        />
                      </div>
                    )}
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      {image.creative_analysis?.overall_score != null ? (
                        <span
                          className="inline-flex items-center gap-1 font-semibold"
                          style={{
                            color: image.creative_analysis.overall_score >= 7.5
                              ? '#10B981'
                              : image.creative_analysis.overall_score >= 5.0
                              ? '#F59E0B'
                              : '#EF4444',
                          }}
                        >
                          <span className="text-[10px]">&#x1F4CA;</span>
                          {image.creative_analysis.overall_score.toFixed(1)}
                        </span>
                      ) : (
                        <span />
                      )}
                      <span>{new Date(image.created_at).toLocaleDateString('ja-JP')}</span>
                    </div>
                    {historyView !== 'my' && image.user && (
                      <div className="mt-2 flex items-center gap-1 text-xs text-gray-500">
                        <User className="w-3 h-3" />
                        <span>{image.user.name || image.user.email}</span>
                      </div>
                    )}
                  </div>
                </div>
                );
              })}
            </div>
            )}

            {currentLoading && (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            )}
            {currentLoading && currentContent.length > 0 && (
              <div className="sticky bottom-4 z-20 flex justify-center pointer-events-none">
                <div className="flex items-center gap-2 rounded-full bg-white/95 px-4 py-2 shadow-lg border border-warm-border text-sm text-gray-700">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                  追加読み込み中
                </div>
              </div>
            )}
            {/* 一括選択で1枚以上選ぶと、下にまとめてタグを付けるバーを出す。
                バーは画面に固定で浮くので、その高さ分の空きを一覧の後ろに足し、最後の行がバーに隠れないようにする */}
            {selectMode && selectModeIds.size > 0 && bannerTags.enabled && (
              <>
                <div aria-hidden="true" className="h-24" />
                {/* スマホ幅では右下にサポートのチャットのボタンが浮くので、バーは左に寄せる */}
                <div className="pointer-events-none fixed inset-x-0 bottom-6 z-30 flex justify-start px-4 sm:justify-center">
                  <BulkTagBar
                    controller={bannerTags}
                    targets={currentContent.filter(image => selectModeIds.has(image.id))}
                    onDone={() => {
                      setSelectMode(false);
                      setSelectModeIds(new Set());
                    }}
                  />
                </div>
              </>
            )}
          </TaskyPageContainer>
        </div>
      </div>

      {/* Image Detail Modal */}
      {selectedImage && (
        <BannerVideoDialog
          onClose={() => setSelectedImage(null)}
          editorOpen={
            showImageEditModal || showMagicResizeModal || showLayerModal
          }
        >
          <BannerVideoWorkspace
            key={selectedImage.id}
            image={selectedImage}
            title={generatedImageTitle(selectedImage)}
            onClose={() => setSelectedImage(null)}
            imageControls={
              <>
                <button
                  onClick={() => handleToggleImageFavorite(selectedImage)}
                  className={`absolute top-6 left-6 h-10 w-10 rounded-md shadow-md flex items-center justify-center z-10 ${
                    selectedImage.is_favorited
                      ? 'bg-rose-500 text-white hover:bg-rose-600'
                      : 'bg-white hover:bg-gray-100'
                  }`}
                  title={
                    selectedImage.is_favorited
                      ? 'お気に入りを解除'
                      : 'お気に入りに追加'
                  }
                >
                  <Heart
                    className={`w-5 h-5 ${selectedImage.is_favorited ? 'fill-current' : ''}`}
                  />
                </button>
                <ImageVersionNavigator
                  image={selectedImage}
                  onSelect={(version) => setSelectedImage(version)}
                />
              </>
            }
            renderImagePanel={(videoActions) => (
              <BannerDetailPanel
                image={selectedImage}
                tags={<ImageTagList controller={bannerTags} image={selectedImage} />}
                onClose={() => setSelectedImage(null)}
                onUsePrompt={() => {
                  setInputMode('freetext');
                  setStyleVariationSource(null);
                  setSelectedStyle(null);
                  setSelectedStyles([]);
                  setStyleMode('none');
                  setPrompt(selectedImage.prompt);
                  setSelectedImage(null);
                  setTimeout(() => {
                    promptSectionRef.current?.scrollIntoView({
                      behavior: 'smooth',
                      block: 'start',
                    });
                  }, 100);
                }}
                onUseImage={() => handleUseImageAsReference(selectedImage)}
                onGenerateStyleVariations={() =>
                  handleGenerateStyleVariations(selectedImage)
                }
                onAutoAbTest={
                  organization?.enable_auto_banner_ab_test
                    ? () => setAutoAbTestImageId(selectedImage.id)
                    : undefined
                }
                {...videoActions}
                onConvertToVideo={
                  selectedImage.s3_key
                    ? videoActions.onConvertToVideo
                    : undefined
                }
                onImageEdit={() => setShowImageEditModal(true)}
                onResize={() => setShowMagicResizeModal(true)}
                onLayers={() => setShowLayerModal(true)}
                onDownload={() =>
                  handleDownloadImage(
                    selectedImage.id,
                    selectedImage.url,
                    selectedImage.prompt
                  )
                }
                onDelete={() => {
                  handleDeleteImage(selectedImage.id);
                  setSelectedImage(null);
                }}
                showPromptButton={
                  shouldShowPrompt() || selectedImage.input_mode === 'freetext'
                }
                onStyleRated={(rating) =>
                  updateImageStyleRating(selectedImage.id, rating)
                }
              />
            )}
          />
        </BannerVideoDialog>
      )}

      {/* Job Detail Modal (for viewing histogram from image detail) */}
      {jobDetailJobId && (
        <JobDetailModal
          jobId={jobDetailJobId}
          isOpen={true}
          onClose={() => setJobDetailJobId(null)}
        />
      )}

      {/* Modals */}
      {showImageSelectionModal && (
        <ProductImageSelectionModal
          isOpen={showImageSelectionModal}
          onClose={() => setShowImageSelectionModal(false)}
          onSelect={handleReferenceImageSelectionConfirm}
          maxSelection={5}
        />
      )}

      {showBannerLibraryModal && (
        <BannerLibraryModal
          isOpen={showBannerLibraryModal}
          onClose={() => setShowBannerLibraryModal(false)}
          onSelect={handleBannerSelect}
          maxSelection={5}
        />
      )}

      {showProductInfoModal && (
        <ProductInfoCopyModal
          isOpen={showProductInfoModal}
          onClose={() => setShowProductInfoModal(false)}
          onCopyToPrompt={handleCopyToPrompt}
        />
      )}

      {showStyleSelectModal && (
        <StyleSelectModal
          isOpen={showStyleSelectModal}
          onClose={() => setShowStyleSelectModal(false)}
          onSelect={(style) => { handleStyleSelect(style); setShowStyleSelectModal(false); }}
          onSelectMany={(styles) => { handleStyleSelectMany(styles); setShowStyleSelectModal(false); }}
          currentStyleId={selectedStyle?.id}
          currentStyleIds={selectedStyles.map(style => style.id)}
          // フリーテキストでは前に選んだ商品を渡さない（推薦と業種タブを商品に寄せない）
          productId={inputMode === 'product' ? selectedProductData?.product?.id ?? null : null}
          cacheKey="banner"
          multiSelect
        />
      )}

      {showProductSelectionModal && (
        <ProductSelectionModal
          isOpen={showProductSelectionModal}
          onClose={() => setShowProductSelectionModal(false)}
          onConfirm={handleProductSelectionConfirm}
        />
      )}

      {/* Image Edit Modal (unified) */}
      {selectedImage && showImageEditModal && (
        <BannerImageEditModal
          image={selectedImage}
          isOpen={showImageEditModal}
          onClose={() => setShowImageEditModal(false)}
          onApply={(editedImage) => {
            showToast('編集した画像を履歴に保存しました。「ダウンロード」で端末に保存できます。');
            setSelectedImage(editedImage);
            setShowImageEditModal(false);
            setDisplayedContent(prev => [editedImage, ...prev]);
          }}
        />
      )}

      {/* Magic Resize Modal */}
      <MagicResizeModal
        isOpen={showMagicResizeModal}
        onClose={() => setShowMagicResizeModal(false)}
        imageId={selectedImage?.id ?? null}
        imageUrl={selectedImage?.url ?? null}
        generationSource="banner_ads"
        onSuccess={() => {
          setJobsRefreshTrigger((prev) => prev + 1);
          setShowMagicResizeModal(false);
          setSelectedImage(null);
        }}
      />
      {/* レイヤー分解 / PSD 書き出し */}
      <LayerExportModal
        isOpen={showLayerModal}
        onClose={() => setShowLayerModal(false)}
        imageId={selectedImage?.id ?? null}
        imageUrl={selectedImage?.url}
        baseFileName={selectedImage ? `banner_${selectedImage.id}` : undefined}
      />
      <BannerOnboardingGuideModal
        isOpen={showBannerGuide}
        onClose={dismissBannerGuide}
        onStart={dismissBannerGuide}
      />
      <CelebrationConfetti
        active={celebrationActive}
        onComplete={() => setCelebrationActive(false)}
      />
      <BannerAbTestStartModal
        open={abTestOpen}
        onClose={() => setAbTestOpen(false)}
        imageIds={abSelectedIds}
        onStarted={(id) => {
          setAbTestOpen(false);
          updateAbSelection([]);
          setAbTestMode(false);
          navigate(`/ads/banner-ab-tests/${id}`);
        }}
      />
      {autoAbTestImageId !== null && (
        <BannerAbTestStartModal
          open
          mode="auto_loop"
          championImageId={autoAbTestImageId}
          onClose={() => setAutoAbTestImageId(null)}
          onStarted={(id) => {
            setAutoAbTestImageId(null);
            setSelectedImage(null);
            navigate(`/ads/banner-ab-tests/${id}`);
          }}
        />
      )}
    </ResponsiveAppShell>
  );
};

export default BannerAdsPage;
